/**
 * My Laps Feature Module
 * Handles lap storage, display, analysis, and setup recommendations
 *
 * Migrated from js/laps.js to strict TypeScript.
 */

// Force this file to be treated as a module so `declare global` works.
export {};

// Shorthand aliases for globals declared in globals.d.ts
const apiUrl = window.apiUrl;

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface WeatherData {
    track_temp: number | null;
    air_temp: number | null;
    skies: string | null;
    weather_type: string;
    humidity: number | null;
    wind_speed: number | null;
}

interface AnalysisIssue {
    name: string;
    severity: string | number;
    description?: string;
    confidence?: number;
    type?: string;
    phase?: string;
    evidence?: string;
}

interface DrivingRecommendationCorner {
    corner_number: number;
    location: {
        direction: string;
        speed_category: string;
        min_speed: number;
        start_pct: number;
    };
    issues_found: {
        name: string;
        type?: string;
        severity: number;
        phase: string;
        evidence: string;
    }[];
    recommendations: {
        entry?: { advice: string; priority: string }[];
        mid?: { advice: string; priority: string }[];
        exit?: { advice: string; priority: string }[];
        general?: { advice: string; priority: string }[];
    };
    priority_focus?: { issue: string };
}

interface DrivingRecommendations {
    total_corners_analyzed: number;
    corners_with_issues: number;
    corners: DrivingRecommendationCorner[];
    summary?: {
        focus_areas?: string[];
        general_advice?: string;
        most_problematic_corners?: { corner: number; location_pct: number; issue_count: number }[];
    };
}

interface LapAnalysis {
    issues?: AnalysisIssue[];
    issue_keys?: string[];
    summary?: Record<string, unknown>;
    driving_recommendations?: DrivingRecommendations;
    top_setup_recommendations?: unknown[];
}

interface LapData {
    id: string;
    lap_number: number;
    lap_time: number;
    track: string;
    car_id: string;
    car_name: string;
    user_id?: number;
    owner_id?: number;
    owner_email?: string;
    created_at?: string;
    added?: string;
    session_time?: string;
    csv_data?: string;
    has_telemetry?: boolean;
    data_points?: number;
    linked_setup_id?: string;
    setup_id?: string;
    setup_name?: string;
    session_type?: string;
    platform?: string;
    analysis?: LapAnalysis;
    weather?: WeatherData;
    notes?: string;
    source?: string;
}

interface TeamMember {
    id: number;
    email: string;
    name?: string;
    online?: boolean;
    live?: boolean;
}

interface TeamViewState {
    enabled: boolean;
    teamId: string | null;
    members: TeamMember[];
    memberId: string | null;
}

interface AdminViewState {
    enabled: boolean;
    allLaps: LapData[];
}

interface SortState {
    column: string | null;
    direction: 'asc' | 'desc';
    groupKey: string | null;
}

interface LapGroup {
    track: string;
    car: string;
    laps: LapData[];
    bestTime: number;
}

interface TelemetrySeriesDefinition {
    id: string;
    label: string;
    key: string;
    color: string;
    range: 'zeroToMax' | 'minMax';
    max?: number;
}

interface TelemetryPoint {
    sessionTime: number;
    speed: number;
    yawRate: number;
    latAccel: number;
    longAccel: number;
    vertAccel: number;
    lapDistPct: number;
    throttle: number;
    brake: number;
    clutch: number;
    steering: number;
    gear: number;
    rpm: number;
    absActive: number;
    latitude: number;
    longitude: number;
    altitude: number;
    yaw: number;
    pitch: number;
    pitchRate: number;
    roll: number;
    rollRate: number;
    tcActive: number;
    tc2Active: number;
    LFtempCL: number;
    LFtempCM: number;
    LFtempCR: number;
    RFtempCL: number;
    RFtempCM: number;
    RFtempCR: number;
    LRtempCL: number;
    LRtempCM: number;
    LRtempCR: number;
    RRtempCL: number;
    RRtempCM: number;
    RRtempCR: number;
    LFpressure: number;
    RFpressure: number;
    LRpressure: number;
    RRpressure: number;
    LFspeed: number;
    RFspeed: number;
    LRspeed: number;
    RRspeed: number;
    LFshockDefl: number;
    RFshockDefl: number;
    LRshockDefl: number;
    RRshockDefl: number;
    waterTemp: number;
    oilTemp: number;
    oilPress: number;
    voltage: number;
    [key: string]: number | undefined;
}

interface MapPoint {
    x: number;
    y: number;
    lapDistPct?: number;
}

interface CornerLabel {
    label: string;
    point: MapPoint;
}

interface ReplayPath {
    points: MapPoint[];
    sampleTimes: number[];
    duration: number;
    baseMapPoints: MapPoint[];
    mapType: 'official' | 'gps' | 'approx';
}

interface TrackMapData {
    points: MapPoint[];
    cornerLabels: CornerLabel[];
    osmData?: OsmData;
}

interface OsmData {
    centerline: MapPoint[];
    features: OsmFeature[];
    ribbon?: RibbonData;
    avg_width_meters?: number;
}

interface OsmFeature {
    type: string;
    name?: string;
    polygon?: MapPoint[];
    center?: MapPoint;
    radius?: number;
}

interface RibbonData {
    inner: MapPoint[];
    outer: MapPoint[];
}

interface ProcrustesTransform {
    scale: number;
    cosT: number;
    sinT: number;
    osmCx: number;
    osmCy: number;
    mapCx: number;
    mapCy: number;
}

interface AlignedOsm {
    ribbon: RibbonData | null;
    features: OsmFeature[];
}

interface ScaleData {
    minX: number;
    minY: number;
    spanX: number;
    spanY: number;
    scale: number;
    offsetX: number;
    offsetY: number;
}

interface CoachMessage {
    time: number;
    text: string;
}

interface TelemetryHandle {
    unmount(): void;
    addLap(lapId: string): void;
    removeLap(lapId: string): void;
    setCursor(distPct: number): void;
}

interface LapReplayState {
    playing: boolean;
    timerId: number | null;
    lastFrameTime: number;
    elapsedTime: number;
    currentIndex: number;
    points: MapPoint[];
    sampleTimes: number[];
    canvas: HTMLCanvasElement | null;
    ctx: CanvasRenderingContext2D | null;
    scale: number;
    offsetX: number;
    offsetY: number;
    bounds: { minX: number; maxX: number; minY: number; maxY: number } | null;
    baseMapPoints: MapPoint[];
    mapType: string;
    telemetrySeries: TelemetryPoint[];
    cornerLabels: CornerLabel[];
    visibleTelemetrySeries: string[];
    coachMessages: CoachMessage[];
    coachIndex: number;
    coachVoiceEnabled: boolean;
    resizeObserver: ResizeObserver | null;
    resizeHandler: (() => void) | null;
    resizeTimeoutId: ReturnType<typeof setTimeout> | null;
    zoom: number;
    panX: number;
    panY: number;
    isPanning: boolean;
    panStartX: number;
    panStartY: number;
    panStartPanX: number;
    panStartPanY: number;
    zoomListenersAttached: boolean;
    osmAligned: AlignedOsm | null;
    _telemetryHandle?: TelemetryHandle | null;
}

interface MiniMapState {
    dismissed: boolean;
    visible: boolean;
    canvas: HTMLCanvasElement | null;
    ctx: CanvasRenderingContext2D | null;
}

interface TelemetryRange {
    min: number;
    max: number;
    span: number;
}

interface SetupItem {
    id: string;
    setup_name?: string;
    name?: string;
    track?: string;
    settings?: Record<string, unknown>;
}

interface Recommendation {
    setting?: string;
    title?: string;
    priority?: string;
    current_value?: string;
    suggested_value?: string;
    change?: string;
    direction?: string;
    amount?: string;
    explanation?: string;
    multi_issue?: boolean;
    addresses?: string;
    track_tips?: Record<string, string>;
}

interface TrackInfo {
    name: string;
    matched: boolean;
    characteristics?: string[];
}

interface EndpointSession {
    track?: {
        id?: string;
        name?: string;
        display_name?: string;
    };
    car?: {
        id?: string;
        name?: string;
        display_name?: string;
    };
}

interface CoachingImportResult {
    success: boolean;
    issuesImported?: number;
    top3?: { id: string; description: string; location?: string; priority?: string }[];
    patterns?: number;
    error?: string;
}

interface CoachingSessionSummary {
    trackId: string;
    trackName: string | null;
    lapsAnalyzed: number;
    totalIssues: number;
    patternCount: number;
    multiLocationCount: number;
    topPatterns: { type: string; cornerNumber: number; position: number; count: number }[];
    multiLocationIssues: { name: string; locationCount: number }[];
    timestamp: string;
}

// Extend Window with all exports from this module
// NOTE: initializeLapsTab, loadLaps, selectLap are declared in globals.d.ts
declare global {
    interface Window {
        // Laps module exports
        lapsData: LapData[];
        analyzeLap: (lapId: string) => Promise<void>;
        showSetupPicker: (lapId: string) => Promise<void>;
        closeSetupPicker: () => void;
        linkSetup: (lapId: string, setupId: string) => Promise<void>;
        getRecommendations: (lapId: string) => Promise<void>;
        deleteLap: (lapId: string, event: Event) => Promise<void>;
        showDrivingRecommendations: (lapId: string) => Promise<void>;
        toggleCornerDetails: (headerElement: HTMLElement) => void;
        toggleLapGroup: (groupIndex: number) => void;
        sortLapsTable: (groupKey: string, column: string, groupIndex: number) => void;
        changeLapPage: (groupKey: string, groupIndex: number, direction: number) => void;
        toggleCompareLap: (lapId: string, event: Event) => void;
        toggleEndpointConnection: () => Promise<void>;
        toggleRecording: () => Promise<void>;
        uploadLastLap: () => Promise<void>;
        uploadBestLap: () => Promise<void>;
        saveFilterPreferences: () => void;
        clearFilterPreferences: () => void;
        openImportLap: () => void;
        closeImportLap: () => void;
        initializeCoachingMemory: (trackId: string, trackName: string, carId: string | null, carName: string | null) => Promise<unknown>;
        startCoachingSession: (trackId: string, trackName: string) => Promise<void>;
        startCoachingLap: (lapNumber: number) => void;
        populateCoachingFromAnalyzedLap: (lapId: string) => Promise<CoachingImportResult>;
        getCurrentCoachingFocus: () => Promise<unknown[]>;
        markCoachingTopicImproved: (topicId: string, notes?: string) => Promise<unknown>;
        showCoachingMemory: () => Promise<void>;
        closeCoachingMemoryModal: () => void;
        initializeCoachingMemoryFromLaps: () => Promise<void>;
        clearCoachingMemory: () => Promise<void>;
        endCoachingSession: () => Promise<CoachingSessionSummary | null>;
        generateCoachingSessionSummary: () => Promise<CoachingSessionSummary | null>;
        checkAndSyncCoachingStatus: () => Promise<void>;
        showCoachingNotification: (title: string, message: string) => void;
        analyzePreviousLapsForCoaching: (trackId: string, carId: string | null, trackName: string, carName: string | null) => Promise<{ analyzed: number; imported: number }>;
        openPitReviewPopup: () => void;

        // External globals this module reads
        showToast?: (message: string, type: string) => void;
        setPredictiveCoaching?: (enabled: boolean) => void;
        onCoachingSessionStarted?: (trackId: string, trackName: string) => void;
        __mountTelemetryChart?: (containerId: string, lapIds: string[]) => TelemetryHandle;
    }
}

// Declare Plotly as a global (optional, loaded externally)
declare const Plotly: {
    react(container: HTMLElement, traces: unknown[], layout: unknown, config: unknown): void;
} | undefined;

// CoachingSession and CoachingTopic are declared globally by coaching_memory.ts
// CoachingSessionData is an alias for local usage
type CoachingSessionData = CoachingSession;

// Declare coachingTracker as a possible global (used with typeof checks)
declare let coachingTracker: CoachingTracker | undefined;

// ---------------------------------------------------------------------------
// State variables
// ---------------------------------------------------------------------------

let lapsData: LapData[] = [];
let selectedLapId: string | null = null;
let comparedLapIds: string[] = [];
let setupsForPicker: SetupItem[] = [];
let lapsInitialized = false;

const ICONS: Record<string, string> = {
    target: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>',
    flag: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg>',
    folder: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"/></svg>',
    trash: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>',
    users: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    eye: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>',
    search: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>',
    wrench: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    car: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>',
    video: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>',
    trendingUp: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>',
    refreshCw: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>',
    lightbulb: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>',
    circleDot: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="1"/></svg>',
    brain: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/><path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/><path d="M3.477 10.896a4 4 0 0 1 .585-.396"/><path d="M19.938 10.5a4 4 0 0 1 .585.396"/><path d="M6 18a4 4 0 0 1-1.967-.516"/><path d="M19.967 17.484A4 4 0 0 1 18 18"/></svg>',
    checkCircle: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>',
    xCircle: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>',
    alertTriangle: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>',
    barChart: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg>',
    thermometer: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/></svg>',
    sun: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>',
    cloudSun: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M12 2v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="M20 12h2"/><path d="m19.07 4.93-1.41 1.41"/><path d="M15.947 12.65a4 4 0 0 0-5.925-4.128"/><path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z"/></svg>',
    cloud: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>',
    pause: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><rect x="14" y="4" width="4" height="16" rx="1"/><rect x="6" y="4" width="4" height="16" rx="1"/></svg>',
    check: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="M20 6 9 17l-5-5"/></svg>',
    circleStop: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>',
    circlePlay: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>',
    layers: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m2 12 8.58 3.91a2 2 0 0 0 1.66 0L21 12"/><path d="m2 17 8.58 3.91a2 2 0 0 0 1.66 0L21 17"/></svg>',
};

// API Token management
let apiToken: string | null = localStorage.getItem('apiToken') || null;

// Endpoint connection state
let endpointConnected = false;
let endpointPollingInterval: ReturnType<typeof setInterval> | null = null;
let endpointCheckInterval: ReturnType<typeof setInterval> | null = null;
let endpointSession: EndpointSession | null = null;
let endpointUsingProxy = false;
const ENDPOINT_URL: string = (window.ENDPOINT_URL || localStorage.getItem('telemetryEndpointUrl') || 'http://localhost:8765');

// Helper to build endpoint URL (direct or proxied)
function getEndpointUrl(path: string): string {
    if (endpointUsingProxy) {
        return `/api/endpoint_proxy?path=${encodeURIComponent(path)}&url=${encodeURIComponent(ENDPOINT_URL)}`;
    }
    return `${ENDPOINT_URL}${path}`;
}

// Helper to parse endpoint response (handles proxy wrapper)
function parseEndpointResponse(data: Record<string, unknown>): Record<string, unknown> {
    if (endpointUsingProxy && data && data.success) {
        return data.data as Record<string, unknown>;
    }
    return data;
}

const teamViewState: TeamViewState = {
    enabled: false,
    teamId: null,
    members: [],
    memberId: null
};

const adminViewState: AdminViewState = {
    enabled: false,
    allLaps: []
};

let presenceInterval: ReturnType<typeof setInterval> | null = null;
let presencePingInterval: ReturnType<typeof setInterval> | null = null;

// Table sorting state
const lapsSortState: SortState = {
    column: null,
    direction: 'asc',
    groupKey: null
};

// Pagination state
const lapsPaginationState: Record<string, number> = {};
const LAPS_PER_PAGE = 10;

function toSafeString(value: unknown, fallback = ''): string {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return fallback;
    try {
        return String(value);
    } catch (_err) {
        return fallback;
    }
}

function toSafeLabel(value: unknown, fallback = 'Unknown'): string {
    const normalized = toSafeString(value).trim();
    return normalized.length ? normalized : fallback;
}

function buildLapsUrl(extraParams: Record<string, string | number | null | undefined> = {}): string {
    const params = new URLSearchParams();
    if (adminViewState.enabled) {
        params.set('admin_view', '1');
    } else if (teamViewState.enabled && teamViewState.teamId) {
        params.set('team_id', teamViewState.teamId);
    }
    Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    });
    const query = params.toString();
    return apiUrl(query ? `/api/laps?${query}` : '/api/laps');
}

function buildMyLapsUrl(extraParams: Record<string, string | number | null | undefined> = {}): string {
    const params = new URLSearchParams();
    Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    });
    const query = params.toString();
    return apiUrl(query ? `/api/laps?${query}` : '/api/laps');
}

function buildTeamLapsUrl(extraParams: Record<string, string | number | null | undefined> = {}): string {
    const params = new URLSearchParams();
    if (teamViewState.teamId) {
        params.set('team_id', teamViewState.teamId);
    }
    Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    });
    const query = params.toString();
    return apiUrl(query ? `/api/laps?${query}` : '/api/laps');
}

function resolveLapUrl(lapId: string): string {
    if (adminViewState.enabled) {
        return buildLapsUrl({ id: lapId });
    }
    const lap = lapsData.find((entry) => entry.id === lapId);
    if (lap && lap.owner_email && teamViewState.teamId) {
        return buildTeamLapsUrl({ id: lapId });
    }
    return buildMyLapsUrl({ id: lapId });
}

/**
 * Get headers with authentication
 */
function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (apiToken) {
        headers['Authorization'] = `Bearer ${apiToken}`;
    }
    return headers;
}

/**
 * Build fetch options with authentication headers and credentials
 */
function authFetchOptions(extra: RequestInit = {}): RequestInit {
    return {
        headers: getAuthHeaders(),
        credentials: 'include',
        ...extra
    };
}

/**
 * Set API token for authenticated requests
 */
function setApiToken(token: string | null): void {
    apiToken = token;
    if (token) {
        localStorage.setItem('apiToken', token);
    } else {
        localStorage.removeItem('apiToken');
    }
}

const trackMapSources: Record<string, string> = {
    limerockpark: '/assets/tracks/lime_rock_park.svg',
    virginiainternationalraceway: '/assets/tracks/vir_full.svg',
    virginiainternationalracewayfullcourse: '/assets/tracks/vir_full.svg',
    summitpointraceway: '/assets/tracks/summit_point.svg',
    summitpoint: '/assets/tracks/summit_point.svg',
    usainternationalspeedway: '/assets/tracks/usa_international.svg',
    roadamerica: '/assets/tracks/road_america.svg',
    homesteadmiamispeedway: '/assets/tracks/homestead_oval.svg',
    homesteadmiamispeedwayoval: '/assets/tracks/homestead_oval.svg',
    homesteadmiamispeedwayroadcourse: '/assets/tracks/homestead_road.svg',
    daytonainternationalspeedway: '/assets/tracks/daytona_oval.svg',
    daytonainternationalspeedwayroadcourse: '/assets/tracks/daytona_road.svg',
    daytonaroadcourse: '/assets/tracks/daytona_road.svg',
    richmondraceway: '/assets/tracks/richmond.svg',
    martinsvillespeedway: '/assets/tracks/martinsville.svg',
    charlottemotorspeedway: '/assets/tracks/charlotte_oval.svg',
    charlottemotorspeedwayroval: '/assets/tracks/charlotte_roval.svg',
    silverstonecircuit: '/assets/tracks/silverstone.svg',
    silverstone: '/assets/tracks/silverstone.svg',
    barbermotorsportspark: '/assets/tracks/barber.svg',
    weathertechracewayatlagunaseca: '/assets/tracks/laguna_seca.svg',
    lagunaseca: '/assets/tracks/laguna_seca.svg',
    sonomaraceway: '/assets/tracks/sonoma.svg',
    sonoma: '/assets/tracks/sonoma.svg',
    atlantamotorspeedway: '/assets/tracks/atlanta.svg',
    themilwaukeemile: '/assets/tracks/milwaukee_mile.svg',
    milwaukeemile: '/assets/tracks/milwaukee_mile.svg',
    sebringinternationalraceway: '/assets/tracks/sebring.svg',
    sebring: '/assets/tracks/sebring.svg',
    bristolmotorspeedway: '/assets/tracks/bristol.svg',
    lasvegasmotorspeedway: '/assets/tracks/las_vegas.svg',
    phoenixinternationalraceway: '/assets/tracks/phoenix_oval.svg',
    phoenixraceway: '/assets/tracks/phoenix_oval.svg',
    ismraceway: '/assets/tracks/phoenix_oval.svg',
    phoenixinternationalracewayroadcourse: '/assets/tracks/phoenix_road.svg',
    watkinsgleninternational: '/assets/tracks/watkins_glen.svg',
    watkinsglen: '/assets/tracks/watkins_glen.svg',
    darlingtonraceway: '/assets/tracks/darlington.svg',
    talladegasuperspeedway: '/assets/tracks/talladega.svg',
    texasmotorspeedway: '/assets/tracks/texas.svg',
    chicagolandspeedway: '/assets/tracks/chicagoland.svg',
    michiganinternationalspeedway: '/assets/tracks/michigan.svg',
    roadatlanta: '/assets/tracks/road_atlanta.svg',
    newhampshiremotorspeedway: '/assets/tracks/new_hampshire.svg',
    indianapolismotorspeedway: '/assets/tracks/indianapolis_oval.svg',
    indianapolismotorspeedwayroadcourse: '/assets/tracks/indianapolis_road.svg',
    poconoraceway: '/assets/tracks/pocono.svg',
    canadiantiremotorsportspark: '/assets/tracks/mosport.svg',
    mosport: '/assets/tracks/mosport.svg',
    brandshatchcircuit: '/assets/tracks/brands_hatch.svg',
    brandshatch: '/assets/tracks/brands_hatch.svg',
    circuitparkzandvoort: '/assets/tracks/zandvoort.svg',
    zandvoort: '/assets/tracks/zandvoort.svg',
    phillipislandcircuit: '/assets/tracks/phillip_island.svg',
    phillipisland: '/assets/tracks/phillip_island.svg',
    phillipislandgrandprixcircuit: '/assets/tracks/phillip_island.svg',
    midohiosportscarcourse: '/assets/tracks/mid_ohio.svg',
    midohio: '/assets/tracks/mid_ohio.svg',
    doverinternationalspeedway: '/assets/tracks/dover.svg',
    dovermotorspeedway: '/assets/tracks/dover.svg',
    circuitdespafrancorchamps: '/assets/tracks/spa.svg',
    spa: '/assets/tracks/spa.svg',
    spafrancorchamps: '/assets/tracks/spa.svg',
    suzukainternationalracingcourse: '/assets/tracks/suzuka.svg',
    suzuka: '/assets/tracks/suzuka.svg',
    iowaspeedway: '/assets/tracks/iowa.svg',
    longbeachstreetcircuit: '/assets/tracks/long_beach.svg',
    longbeach: '/assets/tracks/long_beach.svg',
    oultonparkcircuit: '/assets/tracks/oulton_park.svg',
    oultonpark: '/assets/tracks/oulton_park.svg',
    kentuckyspeedway: '/assets/tracks/kentucky.svg',
    twinringmotegi: '/assets/tracks/motegi_road.svg',
    motegi: '/assets/tracks/motegi_road.svg',
    circuitzolder: '/assets/tracks/zolder.svg',
    zolder: '/assets/tracks/zolder.svg',
    oranparkraceway: '/assets/tracks/oran_park.svg',
    rockinghamspeedway: '/assets/tracks/rockingham.svg',
    autodromojosecarlospace: '/assets/tracks/interlagos.svg',
    interlagos: '/assets/tracks/interlagos.svg',
    kansasspeedway: '/assets/tracks/kansas.svg',
    circuitgillesvilleneuve: '/assets/tracks/gilles_villeneuve.svg',
    mountpanoramacircuit: '/assets/tracks/bathurst.svg',
    bathurst: '/assets/tracks/bathurst.svg',
    autoclubspeedway: '/assets/tracks/auto_club.svg',
    circuitoftheamericas: '/assets/tracks/cota.svg',
    cota: '/assets/tracks/cota.svg',
    doningtonparkracingcircuit: '/assets/tracks/donington.svg',
    doningtonpark: '/assets/tracks/donington.svg',
    worldwidetechnologyraceway: '/assets/tracks/gateway.svg',
    gateway: '/assets/tracks/gateway.svg',
    autodromonazionalemonza: '/assets/tracks/monza.svg',
    monza: '/assets/tracks/monza.svg',
    nurburgringnordschleife: '/assets/tracks/nurburgring_nordschleife.svg',
    nordschleife: '/assets/tracks/nurburgring_nordschleife.svg',
    nurburgringgrandprixstrecke: '/assets/tracks/nurburgring_gp.svg',
    nurburgring: '/assets/tracks/nurburgring_gp.svg',
    nurburgringcombined: '/assets/tracks/nurburgring_nordschleife.svg',
    autodromointernazionaleenzoedinoferrari: '/assets/tracks/imola.svg',
    imola: '/assets/tracks/imola.svg',
    circuitdes24heuresdumans: '/assets/tracks/le_mans.svg',
    lemans: '/assets/tracks/le_mans.svg',
    snetterton: '/assets/tracks/snetterton.svg',
    snettertoncircuit: '/assets/tracks/snetterton.svg',
    detroitgrandprixatbelleisle: '/assets/tracks/belle_isle.svg',
    belleisle: '/assets/tracks/belle_isle.svg',
    tsukubacircuit: '/assets/tracks/tsukuba.svg',
    tsukuba: '/assets/tracks/tsukuba.svg',
    circuitdebarcelonacatalunya: '/assets/tracks/barcelona.svg',
    barcelona: '/assets/tracks/barcelona.svg',
    northwilkesborospeedway: '/assets/tracks/north_wilkesboro.svg',
};

const trackMapCache = new Map<string, TrackMapData | null>();

const telemetrySeriesDefinitions: TelemetrySeriesDefinition[] = [
    { id: 'throttle', label: 'Throttle', key: 'throttle', color: 'rgba(213, 0, 28, 0.9)', range: 'zeroToMax', max: 1 },
    { id: 'brake', label: 'Brake', key: 'brake', color: 'rgba(255, 170, 0, 0.9)', range: 'zeroToMax', max: 1 },
    { id: 'clutch', label: 'Clutch', key: 'clutch', color: 'rgba(156, 39, 176, 0.9)', range: 'zeroToMax', max: 1 },
    { id: 'steering', label: 'Steering', key: 'steering', color: 'rgba(3, 169, 244, 0.9)', range: 'minMax' },
    { id: 'speed', label: 'Speed', key: 'speed', color: 'rgba(76, 175, 80, 0.9)', range: 'zeroToMax' },
    { id: 'rpm', label: 'RPM', key: 'rpm', color: 'rgba(139, 195, 74, 0.9)', range: 'zeroToMax' },
    { id: 'gear', label: 'Gear', key: 'gear', color: 'rgba(255, 193, 7, 0.9)', range: 'zeroToMax' },
    { id: 'latAccel', label: 'Lat G', key: 'latAccel', color: 'rgba(233, 30, 99, 0.9)', range: 'minMax' },
    { id: 'longAccel', label: 'Long G', key: 'longAccel', color: 'rgba(156, 39, 176, 0.9)', range: 'minMax' },
    { id: 'vertAccel', label: 'Vert G', key: 'vertAccel', color: 'rgba(96, 125, 139, 0.9)', range: 'minMax' },
    { id: 'yaw', label: 'Yaw', key: 'yaw', color: 'rgba(0, 188, 212, 0.9)', range: 'minMax' },
    { id: 'yawRate', label: 'Yaw Rate', key: 'yawRate', color: 'rgba(0, 150, 136, 0.9)', range: 'minMax' },
    { id: 'pitch', label: 'Pitch', key: 'pitch', color: 'rgba(255, 87, 34, 0.9)', range: 'minMax' },
    { id: 'pitchRate', label: 'Pitch Rate', key: 'pitchRate', color: 'rgba(255, 152, 0, 0.9)', range: 'minMax' },
    { id: 'roll', label: 'Roll', key: 'roll', color: 'rgba(121, 85, 72, 0.9)', range: 'minMax' },
    { id: 'rollRate', label: 'Roll Rate', key: 'rollRate', color: 'rgba(161, 136, 127, 0.9)', range: 'minMax' },
    { id: 'absActive', label: 'ABS', key: 'absActive', color: 'rgba(244, 67, 54, 0.9)', range: 'zeroToMax', max: 1 },
    { id: 'tcActive', label: 'TC', key: 'tcActive', color: 'rgba(33, 150, 243, 0.9)', range: 'zeroToMax' },
    { id: 'tc2Active', label: 'TC 2', key: 'tc2Active', color: 'rgba(100, 181, 246, 0.9)', range: 'zeroToMax' },
    { id: 'LFtempCL', label: 'LF Temp CL', key: 'LFtempCL', color: 'rgba(239, 83, 80, 0.9)', range: 'zeroToMax' },
    { id: 'LFtempCM', label: 'LF Temp CM', key: 'LFtempCM', color: 'rgba(239, 83, 80, 0.7)', range: 'zeroToMax' },
    { id: 'LFtempCR', label: 'LF Temp CR', key: 'LFtempCR', color: 'rgba(239, 83, 80, 0.5)', range: 'zeroToMax' },
    { id: 'RFtempCL', label: 'RF Temp CL', key: 'RFtempCL', color: 'rgba(102, 187, 106, 0.9)', range: 'zeroToMax' },
    { id: 'RFtempCM', label: 'RF Temp CM', key: 'RFtempCM', color: 'rgba(102, 187, 106, 0.7)', range: 'zeroToMax' },
    { id: 'RFtempCR', label: 'RF Temp CR', key: 'RFtempCR', color: 'rgba(102, 187, 106, 0.5)', range: 'zeroToMax' },
    { id: 'LRtempCL', label: 'LR Temp CL', key: 'LRtempCL', color: 'rgba(66, 165, 245, 0.9)', range: 'zeroToMax' },
    { id: 'LRtempCM', label: 'LR Temp CM', key: 'LRtempCM', color: 'rgba(66, 165, 245, 0.7)', range: 'zeroToMax' },
    { id: 'LRtempCR', label: 'LR Temp CR', key: 'LRtempCR', color: 'rgba(66, 165, 245, 0.5)', range: 'zeroToMax' },
    { id: 'RRtempCL', label: 'RR Temp CL', key: 'RRtempCL', color: 'rgba(255, 167, 38, 0.9)', range: 'zeroToMax' },
    { id: 'RRtempCM', label: 'RR Temp CM', key: 'RRtempCM', color: 'rgba(255, 167, 38, 0.7)', range: 'zeroToMax' },
    { id: 'RRtempCR', label: 'RR Temp CR', key: 'RRtempCR', color: 'rgba(255, 167, 38, 0.5)', range: 'zeroToMax' },
    { id: 'LFpressure', label: 'LF Pressure', key: 'LFpressure', color: 'rgba(239, 83, 80, 0.9)', range: 'zeroToMax' },
    { id: 'RFpressure', label: 'RF Pressure', key: 'RFpressure', color: 'rgba(102, 187, 106, 0.9)', range: 'zeroToMax' },
    { id: 'LRpressure', label: 'LR Pressure', key: 'LRpressure', color: 'rgba(66, 165, 245, 0.9)', range: 'zeroToMax' },
    { id: 'RRpressure', label: 'RR Pressure', key: 'RRpressure', color: 'rgba(255, 167, 38, 0.9)', range: 'zeroToMax' },
    { id: 'LFspeed', label: 'LF Speed', key: 'LFspeed', color: 'rgba(239, 83, 80, 0.9)', range: 'zeroToMax' },
    { id: 'RFspeed', label: 'RF Speed', key: 'RFspeed', color: 'rgba(102, 187, 106, 0.9)', range: 'zeroToMax' },
    { id: 'LRspeed', label: 'LR Speed', key: 'LRspeed', color: 'rgba(66, 165, 245, 0.9)', range: 'zeroToMax' },
    { id: 'RRspeed', label: 'RR Speed', key: 'RRspeed', color: 'rgba(255, 167, 38, 0.9)', range: 'zeroToMax' },
    { id: 'LFshockDefl', label: 'LF Shock Defl', key: 'LFshockDefl', color: 'rgba(239, 83, 80, 0.9)', range: 'minMax' },
    { id: 'RFshockDefl', label: 'RF Shock Defl', key: 'RFshockDefl', color: 'rgba(102, 187, 106, 0.9)', range: 'minMax' },
    { id: 'LRshockDefl', label: 'LR Shock Defl', key: 'LRshockDefl', color: 'rgba(66, 165, 245, 0.9)', range: 'minMax' },
    { id: 'RRshockDefl', label: 'RR Shock Defl', key: 'RRshockDefl', color: 'rgba(255, 167, 38, 0.9)', range: 'minMax' },
    { id: 'waterTemp', label: 'Water Temp', key: 'waterTemp', color: 'rgba(255, 87, 34, 0.9)', range: 'zeroToMax' },
    { id: 'oilTemp', label: 'Oil Temp', key: 'oilTemp', color: 'rgba(255, 193, 7, 0.9)', range: 'zeroToMax' },
    { id: 'oilPress', label: 'Oil Press', key: 'oilPress', color: 'rgba(121, 85, 72, 0.9)', range: 'zeroToMax' },
    { id: 'voltage', label: 'Voltage', key: 'voltage', color: 'rgba(0, 188, 212, 0.9)', range: 'zeroToMax' }
];

// Lap replay state
const lapReplayState: LapReplayState = {
    playing: false,
    timerId: null,
    lastFrameTime: 0,
    elapsedTime: 0,
    currentIndex: 0,
    points: [],
    sampleTimes: [],
    canvas: null,
    ctx: null,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    bounds: null,
    baseMapPoints: [],
    mapType: 'approx',
    telemetrySeries: [],
    cornerLabels: [],
    visibleTelemetrySeries: [],
    coachMessages: [],
    coachIndex: 0,
    coachVoiceEnabled: true,
    resizeObserver: null,
    resizeHandler: null,
    resizeTimeoutId: null,
    zoom: 1,
    panX: 0,
    panY: 0,
    isPanning: false,
    panStartX: 0,
    panStartY: 0,
    panStartPanX: 0,
    panStartPanY: 0,
    zoomListenersAttached: false,
    osmAligned: null
};

const miniMapState: MiniMapState = {
    dismissed: false,
    visible: false,
    canvas: null,
    ctx: null
};

$(document).ready(function(): void {
    initializeLapsTab();
});

function isLapsUIPresent(): boolean {
    return !!(
        document.getElementById('lapsGrid') ||
        document.getElementById('endpointStatusDot') ||
        document.getElementById('filterLapsTrack')
    );
}

function isLapsRoute(): boolean {
    const pathname = (window.location && window.location.pathname ? window.location.pathname : '').replace(/\/+$/, '');
    return pathname === '/laps' || pathname.startsWith('/laps/');
}

function shouldInitializeLapsEndpoint(): boolean {
    return isLapsRoute() || isLapsUIPresent();
}

function autoEnableAdminView(): void {
    const params = new URLSearchParams(window.location.search || '');
    const forceAdminView = params.get('admin') === '1';
    if (forceAdminView && window.currentUser && window.currentUser.is_admin && !adminViewState.enabled) {
        adminViewState.enabled = true;
        const adminToggle = document.getElementById('adminViewAllToggle') as HTMLInputElement | null;
        if (adminToggle) adminToggle.checked = true;
    }
}

function initializeLapsTab(retryCount = 0): void {
    if (!shouldInitializeLapsEndpoint()) {
        return;
    }

    if (!isLapsUIPresent()) {
        if (isLapsRoute() && retryCount < 50) {
            setTimeout(() => initializeLapsTab(retryCount + 1), 100);
        }
        return;
    }

    if (!lapsInitialized) {
        loadSavedFilterPreferences();
        startPresenceHeartbeat();
        initializeLapsFilters();
        initializeLapsEventHandlers();
        checkEndpointAvailable();
        startEndpointAvailabilityCheck();
        lapsInitialized = true;
    }

    autoEnableAdminView();
    loadLaps();
    loadTeams();
}

function loadSavedFilterPreferences(): void {
    try {
        const saved = localStorage.getItem('lapsFilterPreferences');
        if (saved) {
            const prefs = JSON.parse(saved) as Record<string, unknown>;

            if (prefs.adminViewEnabled && window.currentUser && window.currentUser.is_admin) {
                adminViewState.enabled = true;
                const adminToggle = document.getElementById('adminViewAllToggle') as HTMLInputElement | null;
                if (adminToggle) adminToggle.checked = true;
            }

            if (prefs.teamViewEnabled && prefs.teamId) {
                teamViewState.enabled = true;
                teamViewState.teamId = prefs.teamId as string;
                const teamToggle = document.getElementById('teamIncludeToggle') as HTMLInputElement | null;
                if (teamToggle) teamToggle.checked = true;
            }

            if (prefs.trackFilter) {
                setTimeout(() => {
                    const trackSelect = document.getElementById('filterLapsTrack') as HTMLSelectElement | null;
                    if (trackSelect) trackSelect.value = prefs.trackFilter as string;
                }, 1000);
            }
            if (prefs.carFilter) {
                setTimeout(() => {
                    const carSelect = document.getElementById('filterLapsCar') as HTMLSelectElement | null;
                    if (carSelect) carSelect.value = prefs.carFilter as string;
                }, 1000);
            }
        }
    } catch (e) {
        console.error('Error loading filter preferences:', e);
    }
}

function saveFilterPreferences(): void {
    try {
        const prefs = {
            adminViewEnabled: adminViewState.enabled,
            teamViewEnabled: teamViewState.enabled,
            teamId: teamViewState.teamId,
            trackFilter: (document.getElementById('filterLapsTrack') as HTMLSelectElement | null)?.value || '',
            carFilter: (document.getElementById('filterLapsCar') as HTMLSelectElement | null)?.value || ''
        };
        localStorage.setItem('lapsFilterPreferences', JSON.stringify(prefs));
        showLapsNotification('Filter preferences saved as default', 'success');
    } catch (e) {
        console.error('Error saving filter preferences:', e);
        showLapsNotification('Failed to save preferences', 'error');
    }
}

function clearFilterPreferences(): void {
    localStorage.removeItem('lapsFilterPreferences');
    showLapsNotification('Filter preferences cleared', 'success');
}

function showLapsNotification(message: string, type: string = 'info'): void {
    if (window.showToast) {
        window.showToast(message, type);
        return;
    }

    const existing = document.getElementById('laps-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.id = 'laps-notification';
    notification.style.cssText = `
        position: fixed; bottom: 20px; right: 20px; padding: 12px 20px;
        border-radius: 8px; color: white; font-size: 0.9em; z-index: 1000;
        animation: slideIn 0.3s ease;
        background: ${type === 'success' ? '#4CAF50' : type === 'error' ? '#ff4444' : '#333'};
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        setTimeout(() => notification.remove(), 300);
    }, 3000);
}

window.saveFilterPreferences = saveFilterPreferences;
window.clearFilterPreferences = clearFilterPreferences;

function startPresenceHeartbeat(): void {
    if (presencePingInterval) {
        clearInterval(presencePingInterval);
    }
    sendPresencePing();
    presencePingInterval = setInterval(sendPresencePing, 15000);
}

async function sendPresencePing(): Promise<void> {
    try {
        const headers: Record<string, string> = {};
        if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;
        await fetch(apiUrl('/api/presence?action=ping'), {
            method: 'POST',
            credentials: 'include',
            headers,
        });
    } catch (_error) {
        // ignore
    }
}

function startPresencePolling(): void {
    if (presenceInterval) {
        clearInterval(presenceInterval);
    }
    fetchTeamPresence();
    presenceInterval = setInterval(fetchTeamPresence, 10000);
}

function stopPresencePolling(): void {
    if (presenceInterval) {
        clearInterval(presenceInterval);
        presenceInterval = null;
    }
    if (presencePingInterval) {
        clearInterval(presencePingInterval);
        presencePingInterval = null;
    }
    updateTeamPresenceUI([]);
}

async function fetchTeamPresence(): Promise<void> {
    if (!teamViewState.enabled || !teamViewState.teamId) {
        updateTeamPresenceUI([]);
        return;
    }
    try {
        const response = await fetch(apiUrl(`/api/presence?action=team&team_id=${encodeURIComponent(teamViewState.teamId)}`), authFetchOptions({
            method: 'GET'
        }));
        const data = await response.json();
        if (data.success && Array.isArray(data.members)) {
            updateTeamPresenceUI(data.members as TeamMember[]);
            updateTeamMemberSelectPresence(data.members as TeamMember[]);
        }
    } catch (_error) {
        // ignore
    }
}

function updateTeamPresenceUI(members: TeamMember[]): void {
    const container = document.getElementById('teamPresenceList');
    if (!container) return;

    if (!teamViewState.enabled || !teamViewState.teamId || members.length === 0) {
        container.innerHTML = '';
        return;
    }

    const rows = members.map((member) => {
        const liveLabel = member.live ? 'Live' : 'Idle';
        const onlineLabel = member.online ? 'Online' : 'Offline';
        const liveClass = member.live ? 'status-live' : 'status-idle';
        const onlineClass = member.online ? 'status-online' : 'status-offline';
        return `
            <div class="team-presence-row">
                <span class="team-presence-email">${escapeHtml(member.email)}</span>
                <span class="team-presence-pill ${liveClass}">${liveLabel}</span>
                <span class="team-presence-pill ${onlineClass}">${onlineLabel}</span>
            </div>
        `;
    }).join('');

    container.innerHTML = rows;
}

function updateTeamMemberSelectPresence(members: TeamMember[]): void {
    const memberSelect = document.getElementById('teamMemberSelect') as HTMLSelectElement | null;
    if (!memberSelect) return;

    const currentValue = memberSelect.value;
    const options = Array.from(memberSelect.options);
    options.forEach((option) => {
        if (!option.value) return;
        const member = members.find((entry) => String(entry.id) === option.value);
        if (!member) return;
        let suffix = '';
        if (member.live) {
            suffix = ' (live)';
        } else if (member.online) {
            suffix = ' (online)';
        }
        option.textContent = member.email + suffix;
    });

    if (currentValue) {
        memberSelect.value = currentValue;
    }
}

// ============= ENDPOINT CONNECTION =============

async function checkEndpointAvailable(): Promise<void> {
    if (!shouldInitializeLapsEndpoint()) return;

    try {
        const response = await fetch(`${ENDPOINT_URL}/status`);
        if (response.ok) {
            console.log('[Endpoint] Available via direct connection, auto-connecting...');
            await connectEndpoint();
            return;
        }
    } catch (_error) { /* Direct connection failed */ }

    try {
        const response = await fetch(`/api/endpoint_proxy?path=/status&url=${encodeURIComponent(ENDPOINT_URL)}`, authFetchOptions());
        if (response.ok) {
            const result = await response.json();
            if (result.success) {
                console.log('[Endpoint] Available via proxy, auto-connecting...');
                await connectEndpoint(true);
                return;
            }
        }
    } catch (_error) { /* Proxy also failed */ }

    updateEndpointUI(false, false);
}

function startEndpointAvailabilityCheck(): void {
    if (!shouldInitializeLapsEndpoint()) return;
    if (endpointCheckInterval) clearInterval(endpointCheckInterval);
    endpointCheckInterval = setInterval(async () => {
        if (!endpointConnected) await checkEndpointAvailable();
    }, 3000);
}

function stopEndpointAvailabilityCheck(): void {
    if (endpointCheckInterval) {
        clearInterval(endpointCheckInterval);
        endpointCheckInterval = null;
    }
}

async function toggleEndpointConnection(): Promise<void> {
    if (endpointConnected) {
        disconnectEndpoint();
    } else {
        await connectEndpoint();
    }
}

async function connectEndpoint(forceProxy = false): Promise<void> {
    const statusDot = $('#endpointStatusDot');
    const connectBtn = $('#endpointConnectBtn');
    statusDot.removeClass('connected').addClass('connecting');
    connectBtn.text('Connecting...').prop('disabled', true);

    if (!forceProxy && !endpointUsingProxy) endpointUsingProxy = false;

    if (forceProxy || endpointUsingProxy) {
        endpointUsingProxy = true;
        try {
            await tryProxyConnection();
            return;
        } catch (proxyError) {
            console.error('Proxy connection failed:', proxyError);
            endpointUsingProxy = false;
            statusDot.removeClass('connecting');
            connectBtn.text('Connect').prop('disabled', false);
            alert('Could not connect to telemetry endpoint.\n\nMake sure:\n1. The Windows telemetry endpoint is running\n2. iRacing is running and in a session\n\nEndpoint URL: ' + ENDPOINT_URL);
            return;
        }
    }

    try {
        await tryDirectConnection();
    } catch (directError) {
        console.log('Direct connection failed, trying proxy mode...', directError);
        try {
            endpointUsingProxy = true;
            await tryProxyConnection();
        } catch (proxyError) {
            console.error('Proxy connection also failed:', proxyError);
            endpointUsingProxy = false;
            statusDot.removeClass('connecting');
            connectBtn.text('Connect').prop('disabled', false);
            alert('Could not connect to telemetry endpoint.\n\nMake sure:\n1. The Windows telemetry endpoint is running\n2. iRacing is running and in a session\n\nEndpoint URL: ' + ENDPOINT_URL);
        }
    }
}

async function tryDirectConnection(): Promise<void> {
    const statusResponse = await fetch(getEndpointUrl("/status"));
    if (!statusResponse.ok) throw new Error('Endpoint not responding');
    const status = await statusResponse.json();
    const sessionResponse = await fetch(getEndpointUrl("/session"));
    if (sessionResponse.ok) endpointSession = await sessionResponse.json();
    endpointConnected = true;
    updateEndpointUI(status.connected, true);
    await checkAndSyncCoachingStatus();
    startEndpointPolling();
    stopEndpointAvailabilityCheck();
    console.log('Connected to telemetry endpoint (direct)');
}

async function checkAndSyncCoachingStatus(): Promise<void> {
    try {
        const coachingResponse = await fetch(getEndpointUrl("/coaching/status"));
        if (coachingResponse.ok) {
            const coachingStatus = await coachingResponse.json();
            if (coachingStatus.enabled &&
                typeof window.predictiveCoachingConfig !== 'undefined' &&
                !window.predictiveCoachingConfig?.enabled) {
                console.log('[Coaching] Windows client has coaching enabled - auto-activating WebUI');
                if (typeof window.setPredictiveCoaching === 'function') window.setPredictiveCoaching(true);
                const checkbox = document.getElementById('predictiveCoachingToggle') as HTMLInputElement | null;
                if (checkbox) checkbox.checked = true;
                showCoachingNotification(ICONS.target + ' Coaching Auto-Activated', 'Windows client coaching detected and enabled in WebUI');
            }
        }
    } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        console.log('[Coaching] Could not check coaching status:', msg);
    }
}

function showCoachingNotification(title: string, message: string): void {
    const notification = document.createElement('div');
    notification.className = 'coaching-notification-toast';
    notification.innerHTML = `
        <div class="notification-title">${title}</div>
        <div class="notification-message">${escapeHtml(message)}</div>
    `;
    notification.style.cssText = `position: fixed; top: 20px; right: 20px; background: linear-gradient(135deg, #d5001c 0%, #9b0000 100%); color: white; padding: 16px 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); z-index: 10000; max-width: 300px; animation: slideIn 0.3s ease-out;`;
    document.body.appendChild(notification);
    setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => notification.remove(), 300);
    }, 5000);
}

async function tryProxyConnection(): Promise<void> {
    const statusResponse = await fetch(`/api/endpoint_proxy?path=/status&url=${encodeURIComponent(ENDPOINT_URL)}`, authFetchOptions());
    if (!statusResponse.ok) {
        const errorData = await statusResponse.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Proxy connection failed');
    }
    const result = await statusResponse.json();
    if (!result.success) throw new Error(result.message || 'Proxy request failed');
    const status = result.data;
    const sessionResponse = await fetch(`/api/endpoint_proxy?path=/session&url=${encodeURIComponent(ENDPOINT_URL)}`, authFetchOptions());
    if (sessionResponse.ok) {
        const sessionResult = await sessionResponse.json();
        if (sessionResult.success) endpointSession = sessionResult.data;
    }
    endpointConnected = true;
    updateEndpointUI(status.connected, true);
    await checkAndSyncCoachingStatus();
    startEndpointPolling();
    stopEndpointAvailabilityCheck();
    console.log('Connected to telemetry endpoint (via proxy)');
}

function disconnectEndpoint(): void {
    endCoachingSession().then(() => { console.log('[Coaching] Session ended on disconnect'); });
    endpointConnected = false;
    endpointSession = null;
    if (endpointPollingInterval) { clearInterval(endpointPollingInterval); endpointPollingInterval = null; }
    startEndpointAvailabilityCheck();
    updateEndpointUI(false, true);
    console.log('Disconnected from telemetry endpoint');
}

function startEndpointPolling(): void {
    if (endpointPollingInterval) clearInterval(endpointPollingInterval);
    endpointPollingInterval = setInterval(async () => {
        if (!endpointConnected) return;
        try {
            const parseResponse = async (response: Response): Promise<Record<string, unknown>> => {
                const data = await response.json();
                if (endpointUsingProxy && data.success) return data.data;
                return data;
            };
            const statusResponse = await fetch(getEndpointUrl("/status"));
            if (!statusResponse.ok) throw new Error('Endpoint not responding');
            const status = await parseResponse(statusResponse);
            $('#endpointRecording').text(status.recording ? 'Yes' : 'No');
            $('#recordingBtnText').html(status.recording ? ICONS.circleStop + ' Stop Recording' : ICONS.circlePlay + ' Start Recording');
            const lapsResp = await fetch(getEndpointUrl("/lap/all"));
            if (lapsResp.ok) { const ld = await parseResponse(lapsResp); $('#endpointLapCount').text(String(ld.count || 0)); }
            updateEndpointUI(status.connected as boolean, true);
            if (status.connected) {
                const sessResp = await fetch(getEndpointUrl("/session"));
                if (sessResp.ok) {
                    endpointSession = await parseResponse(sessResp) as EndpointSession;
                    $('#endpointTrack').text(endpointSession.track?.display_name || endpointSession.track?.name || 'Unknown');
                    $('#endpointCar').text(endpointSession.car?.display_name || endpointSession.car?.name || 'Unknown');
                }
            } else {
                endpointSession = null;
                $('#endpointTrack').text('Waiting for iRacing...');
                $('#endpointCar').text('Waiting for iRacing...');
            }
        } catch (error) { console.error('Polling error:', error); disconnectEndpoint(); }
    }, 3000);
}

function updateEndpointUI(connected: boolean, available: boolean): void {
    const statusDot = $('#endpointStatusDot');
    const connectBtn = $('#endpointConnectBtn');
    const infoSection = $('#endpointInfo');
    const disconnectedSection = $('#endpointDisconnected');
    statusDot.removeClass('connecting');
    if (available) {
        if (connected) {
            statusDot.addClass('connected');
            connectBtn.text('Disconnect').prop('disabled', false);
            infoSection.show(); disconnectedSection.hide();
            if (endpointSession) {
                $('#endpointTrack').text(endpointSession.track?.display_name || endpointSession.track?.name || 'Unknown');
                $('#endpointCar').text(endpointSession.car?.display_name || endpointSession.car?.name || 'Unknown');
            }
        } else {
            statusDot.removeClass('connected');
            connectBtn.text('Disconnect').prop('disabled', false);
            infoSection.show(); disconnectedSection.hide();
            $('#endpointTrack').text('Waiting for iRacing...');
            $('#endpointCar').text('Waiting for iRacing...');
        }
    } else {
        statusDot.removeClass('connected');
        connectBtn.text('Connect').prop('disabled', !available);
        infoSection.hide(); disconnectedSection.show();
    }
}

async function toggleRecording(): Promise<void> {
    if (!endpointConnected) return;
    try {
        const statusResponse = await fetch(getEndpointUrl("/status"));
        const status = parseEndpointResponse(await statusResponse.json());
        if (!status.connected && !status.recording) { alert('iRacing is not connected. Recording requires iRacing to be running.'); return; }
        const endpoint = status.recording ? '/recording/stop' : '/recording/start';
        const response = await fetch(getEndpointUrl(endpoint), { method: 'POST' });
        if (response.ok) {
            const result = parseEndpointResponse(await response.json());
            const isRecording = result.status === 'recording';
            $('#endpointRecording').text(isRecording ? 'Yes' : 'No');
            $('#recordingBtnText').html(isRecording ? ICONS.circleStop + ' Stop Recording' : ICONS.circlePlay + ' Start Recording');
        }
    } catch (error: unknown) {
        console.error('Failed to toggle recording:', error);
        alert('Failed to toggle recording: ' + (error instanceof Error ? error.message : String(error)));
    }
}

async function uploadLastLap(): Promise<void> {
    if (!endpointConnected) { alert('Not connected to telemetry endpoint'); return; }
    try {
        const statusResponse = await fetch(getEndpointUrl("/status"));
        const status = parseEndpointResponse(await statusResponse.json());
        if (!status.connected) { alert('iRacing is not connected. Cannot upload lap data.'); return; }
    } catch (error: unknown) { alert('Failed to check endpoint status: ' + (error instanceof Error ? error.message : String(error))); return; }
    await uploadLapFromEndpoint('last');
}

async function uploadBestLap(): Promise<void> {
    if (!endpointConnected) { alert('Not connected to telemetry endpoint'); return; }
    try {
        const statusResponse = await fetch(getEndpointUrl("/status"));
        const status = parseEndpointResponse(await statusResponse.json());
        if (!status.connected) { alert('iRacing is not connected. Cannot upload lap data.'); return; }
    } catch (error: unknown) { alert('Failed to check endpoint status: ' + (error instanceof Error ? error.message : String(error))); return; }
    await uploadLapFromEndpoint('best');
}

async function uploadLapFromEndpoint(lapType: string): Promise<void> {
    const btn = lapType === 'best'
        ? $("button:contains('Upload Best')")
        : $("button:contains('Upload Last')");

    const originalText = btn.text();
    btn.prop('disabled', true).text('Uploading...');

    try {
        const csvResponse = await fetch(getEndpointUrl(`/lap/${lapType}?format=csv`));

        if (!csvResponse.ok) {
            const errorData: Record<string, string> = await csvResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'No lap data available');
        }

        const csvData = await csvResponse.text();

        if (!csvData || csvData.startsWith('{')) {
            throw new Error('No lap data available');
        }

        const lapInfoResponse = await fetch(getEndpointUrl(`/lap/${lapType}`));
        let lapInfo: Record<string, unknown> = {};
        if (lapInfoResponse.ok) {
            lapInfo = parseEndpointResponse(await lapInfoResponse.json());
        }

        const sessionResponse = await fetch(getEndpointUrl("/session"));
        let session: Record<string, unknown> = {};
        if (sessionResponse.ok) {
            session = parseEndpointResponse(await sessionResponse.json());
        }

        const dataPoints = csvData.split('\n').length - 1;
        const weather = extractWeatherFromCsv(csvData);

        const sessionTrack = session.track as { display_name?: string; name?: string; id?: string } | undefined;
        const sessionCar = session.car as { display_name?: string; name?: string; id?: string } | undefined;

        const lap = {
            lap_number: (lapInfo.lapNumber as number) || (lapInfo.lap_number as number) || 1,
            lap_time: (lapInfo.lapTime as number) || (lapInfo.lap_time as number) || null,
            track: sessionTrack?.display_name || sessionTrack?.name || 'Unknown',
            car_id: sessionCar?.id || sessionCar?.name || 'unknown',
            car_name: sessionCar?.display_name || sessionCar?.name || 'Unknown Car',
            weather: weather,
            session_time: new Date().toISOString(),
            data_points: dataPoints,
            csv_data: csvData,
            analysis: null as null,
            linked_setup_id: null as null,
            source: lapType === 'best' ? 'endpoint_best' : 'endpoint_last'
        };

        const uploadResponse = await fetch(apiUrl('/api/laps'), authFetchOptions({
            method: 'POST',
            body: JSON.stringify({ lap: lap })
        }));

        const result = await uploadResponse.json();

        if (result.success) {
            await loadLaps();

            let coachingImportResult: CoachingImportResult | null = null;

            if (result.lap_id && typeof window.predictiveCoachingConfig !== 'undefined' &&
                window.predictiveCoachingConfig?.enabled) {
                try {
                    console.log('[Coaching] Auto-analyzing uploaded lap:', result.lap_id);

                    const analyzeResponse = await fetch(apiUrl(`/api/laps?action=analyze&id=${result.lap_id}`), authFetchOptions({
                        method: 'POST'
                    }));
                    const analysisResult = await analyzeResponse.json();

                    if (analysisResult.success && analysisResult.analysis) {
                        coachingImportResult = await populateCoachingFromAnalyzedLap(result.lap_id);
                        console.log('[Coaching] Auto-import result:', coachingImportResult);
                    }
                } catch (analyzeError: unknown) {
                    console.warn('[Coaching] Auto-analysis failed:', analyzeError);
                }
            }

            const lapTimeStr = lap.lap_time
                ? formatLapTime(lap.lap_time)
                : 'N/A';
            let message = `${lapType === 'best' ? 'Best' : 'Last'} lap uploaded successfully!\n\n` +
                `Track: ${lap.track}\n` +
                `Lap Time: ${lapTimeStr}`;

            if (coachingImportResult?.success) {
                message += `\n\n` + ICONS.target + ` Coaching: ${coachingImportResult.issuesImported} issues imported`;
                if (coachingImportResult.top3 && coachingImportResult.top3.length > 0) {
                    message += `\n   Top focus areas updated`;
                }
            }

            alert(message);
        } else {
            throw new Error(result.message || 'Upload failed');
        }

    } catch (error: unknown) {
        console.error('Failed to upload lap:', error);
        alert('Failed to upload lap: ' + (error instanceof Error ? error.message : String(error)));
    }

    btn.prop('disabled', false).text(originalText);
}

// ---------------------------------------------------------------------------
// Load laps / teams
// ---------------------------------------------------------------------------

async function loadLaps(): Promise<void> {
    try {
        let combined: LapData[] = [];

        if (adminViewState.enabled) {
            const adminResponse = await fetch(buildLapsUrl(), authFetchOptions({ method: 'GET' }));
            if (adminResponse.status === 401) {
                console.warn('Not authenticated — cannot load laps');
                lapsData = [];
                window.lapsData = lapsData;
                renderLapsGrid();
                return;
            }
            const adminData = await adminResponse.json();
            combined = adminData.success ? (adminData.laps || []) : [];
        } else {
            const myResponse = await fetch(buildMyLapsUrl(), authFetchOptions({ method: 'GET' }));
            if (myResponse.status === 401) {
                console.warn('Not authenticated — cannot load laps');
                lapsData = [];
                window.lapsData = lapsData;
                renderLapsGrid();
                return;
            }
            const myData = await myResponse.json();
            combined = myData.success ? (myData.laps || []) : [];

            if (teamViewState.enabled && teamViewState.teamId) {
                const teamResponse = await fetch(buildTeamLapsUrl(), authFetchOptions({ method: 'GET' }));
                const teamData = await teamResponse.json();
                if (teamData.success && Array.isArray(teamData.laps)) {
                    const seen = new Map<string, LapData>();
                    combined.forEach((lap) => {
                        if (lap && lap.id) {
                            seen.set(lap.id, lap);
                        }
                    });
                    teamData.laps.forEach((lap: LapData) => {
                        if (lap && lap.id && !seen.has(lap.id)) {
                            seen.set(lap.id, lap);
                        }
                    });
                    combined = Array.from(seen.values());
                }
            }
        }

        lapsData = combined;
        window.lapsData = lapsData;
        renderLapsGrid();
        updateLapsTrackFilter();
    } catch (error: unknown) {
        console.error('Error loading laps:', error);
    }
}

async function loadTeams(): Promise<void> {
    try {
        const response = await fetch(apiUrl('/api/teams?action=list'), authFetchOptions({ method: 'GET' }));
        const data = await response.json();
        if (data.success && Array.isArray(data.teams)) {
            populateTeamSelect(data.teams);
        }
    } catch (error: unknown) {
        console.warn('Teams not available:', error);
    }
}

async function loadTeamMembers(): Promise<void> {
    const memberSelect = document.getElementById('teamMemberSelect') as HTMLSelectElement | null;
    if (!memberSelect) return;

    memberSelect.innerHTML = '<option value="">Live: My Feed</option>';
    memberSelect.disabled = true;

    if (!teamViewState.teamId) {
        stopPresencePolling();
        return;
    }

    try {
        const response = await fetch(apiUrl(`/api/teams?action=members&team_id=${encodeURIComponent(teamViewState.teamId)}`), authFetchOptions({ method: 'GET' }));
        const data = await response.json();
        if (data.success && Array.isArray(data.members)) {
            teamViewState.members = data.members;
            data.members.forEach((member: TeamMember) => {
                const option = document.createElement('option');
                option.value = String(member.id);
                option.textContent = member.email;
                memberSelect.appendChild(option);
            });
            memberSelect.disabled = false;
            startPresencePolling();
        }
    } catch (error: unknown) {
        console.warn('Failed to load team members:', error);
    }
}

function populateTeamSelect(teams: { id: string; name: string }[]): void {
    const teamSelect = document.getElementById('teamSelect') as HTMLSelectElement | null;
    if (!teamSelect) return;

    teamSelect.innerHTML = '<option value="">Select Team</option>';
    teams.forEach((team) => {
        const option = document.createElement('option');
        option.value = team.id;
        option.textContent = team.name;
        teamSelect.appendChild(option);
    });
}

// ---------------------------------------------------------------------------
// Render laps grid
// ---------------------------------------------------------------------------

function renderLapsGrid(): void {
    const container = $('#lapsGrid');

    if (lapsData.length === 0) {
        container.html(`
            <div class="empty-state">
                <div class="empty-icon">${ICONS.flag}</div>
                <h3>No Laps Yet</h3>
                <p>Import lap data to get started.</p>
                <button class="btn-primary" onclick="openImportLap()">${ICONS.folder} Import Lap Data</button>
            </div>
        `);
        return;
    }

    const groups = groupLapsByTrackCar(lapsData);

    let html = '';

    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
        const trackA = toSafeString(groups[a].track).toLowerCase();
        const trackB = toSafeString(groups[b].track).toLowerCase();
        return trackA.localeCompare(trackB);
    });

    sortedGroupKeys.forEach((groupKey, groupIndex) => {
        const group = groups[groupKey];
        const bestTime = group.bestTime;

        html += `
            <div class="lap-group">
                <div class="lap-group-header" onclick="toggleLapGroup(${groupIndex})">
                    <div class="lap-group-title">
                        <span class="lap-group-icon">\u25B6</span>
                        <strong>${escapeHtml(group.track)}</strong>
                        <span class="lap-group-car">${escapeHtml(group.car)}</span>
                        <span class="lap-group-count">${group.laps.length} lap${group.laps.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div class="lap-group-best">
                        Best: <span class="best-time">${formatLapTime(bestTime)}</span>
                    </div>
                </div>
                <div class="lap-group-content" id="lap-group-${groupIndex}">
                    <table class="laps-table">
                        <thead>
                            <tr>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'lap_number', ${groupIndex})" data-column="lap_number">Lap # <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'lap_time', ${groupIndex})" data-column="lap_time">Time <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'weather', ${groupIndex})" data-column="weather">Weather <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'track_temp', ${groupIndex})" data-column="track_temp">Track Temp <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'air_temp', ${groupIndex})" data-column="air_temp">Air Temp <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'date', ${groupIndex})" data-column="date">Date <span class="sort-indicator"></span></th>
                                <th class="sortable-header" onclick="sortLapsTable('${escapeJsString(groupKey)}', 'samples', ${groupIndex})" data-column="samples">Samples <span class="sort-indicator"></span></th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${renderLapRows(getPaginatedLaps(group.laps, groupKey), bestTime)}
                        </tbody>
                    </table>
                    ${renderPaginationControls(groupKey, groupIndex, group.laps.length)}
                </div>
            </div>
        `;
    });

    container.html(html);

    const firstGroup = container.find('.lap-group-content').first();
    if (firstGroup.length && !firstGroup.hasClass('expanded')) {
        firstGroup.addClass('expanded');
        const icon = firstGroup.prev().find('.lap-group-icon');
        if (icon.length) icon.text('\u25BC');
    }
}

// ---------------------------------------------------------------------------
// Sort / pagination / grouping
// ---------------------------------------------------------------------------

function sortLapsTable(groupKey: string, column: string, groupIndex: number): void {
    const groups = groupLapsByTrackCar(lapsData);
    const group = groups[groupKey];
    if (!group) return;

    if (lapsSortState.column === column && lapsSortState.groupKey === groupKey) {
        lapsSortState.direction = lapsSortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
        lapsSortState.column = column;
        lapsSortState.groupKey = groupKey;
        lapsSortState.direction = 'asc';
    }

    sortLapsInPlace(group.laps, column, lapsSortState.direction);

    lapsPaginationState[groupKey] = 1;

    const tbody = document.querySelector(`#lap-group-${groupIndex} .laps-table tbody`);
    if (tbody) {
        tbody.innerHTML = renderLapRows(getPaginatedLaps(group.laps, groupKey), group.bestTime);
    }

    const paginationContainer = document.querySelector(`#lap-group-${groupIndex} .laps-pagination`);
    if (paginationContainer) {
        paginationContainer.outerHTML = renderPaginationControls(groupKey, groupIndex, group.laps.length);
    }

    updateSortIndicators(groupIndex, column, lapsSortState.direction);
}

function updateSortIndicators(groupIndex: number, activeColumn: string, direction: string): void {
    const groupContent = document.getElementById(`lap-group-${groupIndex}`);
    if (!groupContent) return;

    const headers = groupContent.querySelectorAll('.sortable-header');
    headers.forEach((header) => {
        const column = header.getAttribute('data-column');
        const indicator = header.querySelector('.sort-indicator');

        if (column === activeColumn) {
            header.classList.add('sorted');
            header.classList.toggle('sorted-asc', direction === 'asc');
            header.classList.toggle('sorted-desc', direction === 'desc');
            if (indicator) {
                indicator.textContent = direction === 'asc' ? ' \u25B2' : ' \u25BC';
            }
        } else {
            header.classList.remove('sorted', 'sorted-asc', 'sorted-desc');
            if (indicator) {
                indicator.textContent = '';
            }
        }
    });
}

function groupLapsByTrackCar(laps: LapData[]): Record<string, LapGroup> {
    const groups: Record<string, LapGroup> = {};

    laps.forEach((lap) => {
        const track = toSafeLabel(lap.track);
        const car = toSafeLabel(lap.car_name || lap.car_id);
        const key = `${track}|${car}`;

        if (!groups[key]) {
            groups[key] = { track, car, laps: [], bestTime: Infinity };
        }
        groups[key].laps.push(lap);
    });

    Object.values(groups).forEach((group) => {
        const positiveTimes = group.laps
            .map((l) => l.lap_time)
            .filter((t): t is number => t !== null && t !== undefined && t > 0)
            .sort((a, b) => a - b);
        const median = positiveTimes.length > 0 ? positiveTimes[Math.floor(positiveTimes.length / 2)] : 0;
        const minTime = median * 0.5;

        for (const lap of group.laps) {
            if (lap.lap_time && lap.lap_time >= minTime && lap.lap_time < group.bestTime) {
                group.bestTime = lap.lap_time;
            }
        }

        group.laps.sort((a, b) => {
            const timeA = Number.isFinite(Number(a.lap_time)) ? Number(a.lap_time) : Infinity;
            const timeB = Number.isFinite(Number(b.lap_time)) ? Number(b.lap_time) : Infinity;
            return timeA - timeB;
        });
    });

    return groups;
}

function renderLapRows(laps: LapData[], bestTime: number): string {
    return laps.map((lap) => {
        const lapTime = Number(lap.lap_time);
        const lapTimeFormatted = formatLapTime(lapTime);
        const isBestTime = Number.isFinite(lapTime) && lapTime === bestTime && bestTime < Infinity;
        const isSelected = lap.id === selectedLapId;
        const dateFormatted = formatDateShort(lap.session_time || lap.added);
        const deleteButton = lap.owner_email ? '' : `<button class="btn-icon btn-small" onclick="deleteLap('${lap.id}', event)" title="Delete">${ICONS.trash}</button>`;
        const hasData = (lap.data_points || 0) > 0;
        const isCompared = comparedLapIds.includes(lap.id);
        const compareButton = (hasData && !isSelected)
            ? `<button class="btn-icon btn-small ${isCompared ? 'btn-compare-active' : ''}" onclick="toggleCompareLap('${lap.id}', event)" title="${isCompared ? 'Remove from overlay' : 'Overlay on chart'}">${ICONS.layers}</button>`
            : '';

        const weather = lap.weather || {} as WeatherData;
        const weatherIcon = getWeatherIcon(weather.skies);
        const trackTempValue = Number(weather.track_temp);
        const airTempValue = Number(weather.air_temp);
        const trackTemp = Number.isFinite(trackTempValue) ? `${trackTempValue.toFixed(1)}\u00B0C` : '-';
        const airTemp = Number.isFinite(airTempValue) ? `${airTempValue.toFixed(1)}\u00B0C` : '-';
        const weatherType = toSafeLabel(weather.weather_type);
        const weatherSkies = toSafeLabel(weather.skies);

        const badges: string[] = [];
        if (lap.linked_setup_id) badges.push('<span class="badge badge-setup">Setup</span>');
        if (lap.analysis) badges.push('<span class="badge badge-analyzed">Analyzed</span>');
        if (lap.owner_email) badges.push('<span class="badge badge-team">' + ICONS.users + ' Team</span>');
        if (adminViewState.enabled && lap.owner_id !== undefined) {
            const ownerLabel = lap.owner_id === 0 ? 'Anonymous' : `User ${lap.owner_id}`;
            badges.push(`<span class="badge badge-admin">${ICONS.eye} ${ownerLabel}</span>`);
        }

        return `
            <tr class="lap-row ${isSelected ? 'selected' : ''} ${isBestTime ? 'best-lap' : ''}"
                data-lap-id="${lap.id}"
                onclick="selectLap('${lap.id}')">
                <td class="lap-number-cell">${lap.lap_number || '?'}</td>
                <td class="lap-time-cell ${isBestTime ? 'best-time-text' : ''}">${lapTimeFormatted}</td>
                <td class="lap-weather-cell" title="${weatherType}">${weatherIcon} ${weatherSkies}</td>
                <td class="lap-temp-cell">${trackTemp}</td>
                <td class="lap-temp-cell">${airTemp}</td>
                <td class="lap-date-cell">${dateFormatted}</td>
                <td class="lap-samples-cell">${lap.data_points || 0}</td>
                <td class="lap-status-cell">${badges.join(' ')}</td>
                <td class="lap-actions-cell" onclick="event.stopPropagation()">${compareButton}${deleteButton}</td>
            </tr>
        `;
    }).join('');
}

function getPaginatedLaps(laps: LapData[], groupKey: string): LapData[] {
    if (laps.length <= LAPS_PER_PAGE) return laps;
    const page = lapsPaginationState[groupKey] || 1;
    const start = (page - 1) * LAPS_PER_PAGE;
    return laps.slice(start, start + LAPS_PER_PAGE);
}

function renderPaginationControls(groupKey: string, groupIndex: number, totalLaps: number): string {
    if (totalLaps <= LAPS_PER_PAGE) return '';
    const currentPage = lapsPaginationState[groupKey] || 1;
    const totalPages = Math.ceil(totalLaps / LAPS_PER_PAGE);
    const escapedKey = escapeJsString(groupKey);
    return `
        <div class="laps-pagination">
            <button class="btn-pagination" onclick="changeLapPage('${escapedKey}', ${groupIndex}, -1)" ${currentPage <= 1 ? 'disabled' : ''}>Prev</button>
            <span class="pagination-info">Page ${currentPage} of ${totalPages}</span>
            <button class="btn-pagination" onclick="changeLapPage('${escapedKey}', ${groupIndex}, 1)" ${currentPage >= totalPages ? 'disabled' : ''}>Next</button>
        </div>
    `;
}

function changeLapPage(groupKey: string, groupIndex: number, direction: number): void {
    const groups = groupLapsByTrackCar(lapsData);
    const group = groups[groupKey];
    if (!group) return;

    const totalPages = Math.ceil(group.laps.length / LAPS_PER_PAGE);
    const currentPage = lapsPaginationState[groupKey] || 1;
    const newPage = currentPage + direction;
    if (newPage < 1 || newPage > totalPages) return;

    lapsPaginationState[groupKey] = newPage;

    if (lapsSortState.groupKey === groupKey && lapsSortState.column) {
        sortLapsInPlace(group.laps, lapsSortState.column, lapsSortState.direction);
    }

    const tbody = document.querySelector(`#lap-group-${groupIndex} .laps-table tbody`);
    if (tbody) {
        tbody.innerHTML = renderLapRows(getPaginatedLaps(group.laps, groupKey), group.bestTime);
    }

    const paginationContainer = document.querySelector(`#lap-group-${groupIndex} .laps-pagination`);
    if (paginationContainer) {
        paginationContainer.outerHTML = renderPaginationControls(groupKey, groupIndex, group.laps.length);
    }
}

function sortLapsInPlace(laps: LapData[], column: string, direction: string): void {
    laps.sort((a, b) => {
        let valA: string | number;
        let valB: string | number;
        switch (column) {
            case 'lap_number': valA = a.lap_number || 0; valB = b.lap_number || 0; break;
            case 'lap_time': valA = Number.isFinite(Number(a.lap_time)) ? Number(a.lap_time) : Infinity; valB = Number.isFinite(Number(b.lap_time)) ? Number(b.lap_time) : Infinity; break;
            case 'weather': valA = toSafeString(a.weather?.skies).toLowerCase(); valB = toSafeString(b.weather?.skies).toLowerCase(); break;
            case 'track_temp': valA = a.weather?.track_temp || -Infinity; valB = b.weather?.track_temp || -Infinity; break;
            case 'air_temp': valA = a.weather?.air_temp || -Infinity; valB = b.weather?.air_temp || -Infinity; break;
            case 'date': valA = new Date(a.session_time || a.added || 0).getTime(); valB = new Date(b.session_time || b.added || 0).getTime(); break;
            case 'samples': valA = a.data_points || 0; valB = b.data_points || 0; break;
            default: return 0;
        }
        if (typeof valA === 'string' && typeof valB === 'string') {
            return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return direction === 'asc' ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
    });
}

// ---------------------------------------------------------------------------
// Utility: weather, escape, format
// ---------------------------------------------------------------------------

function getWeatherIcon(skies: string | null | undefined): string {
    if (!skies) return ICONS.thermometer;
    const skiesLower = toSafeString(skies).toLowerCase();
    if (skiesLower.includes('clear')) return ICONS.sun;
    if (skiesLower.includes('partly')) return ICONS.cloudSun;
    if (skiesLower.includes('mostly')) return ICONS.cloudSun;
    if (skiesLower.includes('overcast')) return ICONS.cloud;
    return ICONS.thermometer;
}

function toggleLapGroup(groupIndex: number): void {
    const content = document.getElementById(`lap-group-${groupIndex}`);
    if (!content) return;
    const header = content.previousElementSibling;
    if (!header) return;
    const icon = header.querySelector('.lap-group-icon');

    if (content.classList.contains('expanded')) {
        content.classList.remove('expanded');
        if (icon) icon.textContent = '\u25B6';
    } else {
        content.classList.add('expanded');
        if (icon) icon.textContent = '\u25BC';
    }
}

function escapeHtml(text: string | null | undefined): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function escapeJsString(text: string | null | undefined): string {
    if (!text) return '';
    return text
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r');
}

function formatDateShort(dateString: string | null | undefined): string {
    if (!dateString) return 'Unknown';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (_e: unknown) {
        return 'Invalid';
    }
}

function extractWeatherFromCsv(csvData: string): WeatherData {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
        return { track_temp: null, air_temp: null, skies: null, weather_type: 'Unknown', humidity: null, wind_speed: null };
    }

    const headers = lines[0].split(',');
    const firstDataLine = lines[1].split(',');

    const getValue = (name: string): string | undefined => {
        const index = headers.indexOf(name);
        return index >= 0 ? firstDataLine[index] : undefined;
    };

    const skiesMap: Record<string, string> = { '0': 'Clear', '1': 'Partly Cloudy', '2': 'Mostly Cloudy', '3': 'Overcast' };

    const skiesValue = getValue('Skies');
    const weatherTypeValue = getValue('WeatherType');

    return {
        track_temp: getValue('TrackTemp') ? parseFloat(getValue('TrackTemp')!) : null,
        air_temp: getValue('AirTemp') ? parseFloat(getValue('AirTemp')!) : null,
        skies: skiesMap[skiesValue || ''] || 'Unknown',
        weather_type: weatherTypeValue ? (parseInt(weatherTypeValue) === 1 ? 'Dynamic' : 'Constant') : 'Unknown',
        humidity: getValue('RelativeHumidity') ? parseFloat(getValue('RelativeHumidity')!) : null,
        wind_speed: getValue('WindVel') ? parseFloat(getValue('WindVel')!) : null
    };
}

function toggleCompareLap(lapId: string, event: Event): void {
    event.stopPropagation();
    const handle = lapReplayState._telemetryHandle;
    if (!handle) return;

    const idx = comparedLapIds.indexOf(lapId);
    if (idx >= 0) {
        comparedLapIds.splice(idx, 1);
        handle.removeLap(lapId);
    } else {
        comparedLapIds.push(lapId);
        handle.addLap(lapId);
    }
    renderLapsGrid();
}

// ---------------------------------------------------------------------------
// Select lap / show details / analyze
// ---------------------------------------------------------------------------

async function selectLap(lapId: string): Promise<void> {
    selectedLapId = lapId;
    comparedLapIds = [];
    renderLapsGrid();

    try {
        const response = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'GET' }));
        const data = await response.json();

        if (data.success && data.lap) {
            showLapDetails(data.lap);
        }
    } catch (error: unknown) {
        console.error('Error fetching lap details:', error);
    }
}

function showLapDetails(lap: LapData): void {
    if (lapReplayState._telemetryHandle) {
        lapReplayState._telemetryHandle.unmount();
        lapReplayState._telemetryHandle = null;
    }

    const container = $('#lapDetailSection');
    const lapTimeFormatted = formatLapTime(lap.lap_time);
    const ownerInfo = lap.owner_email ? `<span>Owner: ${escapeHtml(lap.owner_email)}</span>` : '';
    const isTeamLap = Boolean(lap.owner_email);
    const teamDisabled = isTeamLap ? 'disabled title="Team laps are read-only"' : '';

    let analysisHtml = '';
    if (lap.analysis && lap.analysis.issues) {
        const coachHtml = buildCoachFeedHtml();
        const topIssues = [...(lap.analysis.issues || [])]
            .sort((a, b) => ((b.confidence || 0) as number) - ((a.confidence || 0) as number))
            .slice(0, 3);
        const coachQuickTips = topIssues.length > 0 ? `
            <div class="coach-quick-tips">
                <h5>${ICONS.brain} Quick Coach Tips</h5>
                <ul>
                    ${topIssues.map((issue) => `
                        <li><strong>${escapeHtml(issue.name)}</strong> \u2014 ${escapeHtml(formatCoachTip(issue.name))}</li>
                    `).join('')}
                </ul>
            </div>
        ` : '';
        analysisHtml = `
            <div class="lap-analysis-results">
                <h4>Analysis Results</h4>
                <p>Issues Detected: ${lap.analysis.issues.length}</p>
                <ul class="lap-issues-list">
                    ${lap.analysis.issues.map((issue) => `
                        <li class="lap-issue-item confidence-${getConfidenceClass(issue.confidence || 0)}">
                            <strong>${escapeHtml(issue.name)}</strong>
                            <span class="confidence">${Math.round(issue.confidence || 0)}% confidence</span>
                        </li>
                    `).join('')}
                </ul>
                ${coachQuickTips}
                ${coachHtml}
            </div>
        `;
    }

    let setupHtml = '';
    if (lap.linked_setup_id) {
        let setupName: string = lap.linked_setup_id;
        const linkedSetup = setupsForPicker.find((s) => s.id === lap.linked_setup_id);
        if (linkedSetup) {
            setupName = linkedSetup.setup_name || linkedSetup.name || lap.linked_setup_id;
        } else {
            const localSetups: SetupItem[] = JSON.parse(localStorage.getItem('setupsDatabase') || '[]');
            const localSetup = localSetups.find((s) => s.id === lap.linked_setup_id);
            if (localSetup) {
                setupName = localSetup.setup_name || localSetup.name || lap.linked_setup_id;
            }
        }
        setupHtml = `<p class="linked-setup-info">Linked to setup: ${escapeHtml(setupName)}</p>`;
    }

    const hasDrivingRecs = lap.analysis && lap.analysis.driving_recommendations;

    const weather = lap.weather || {} as WeatherData;
    const weatherIcon = getWeatherIcon(weather.skies);
    const trackTemp = weather.track_temp ? `${weather.track_temp.toFixed(1)}\u00B0C` : 'N/A';
    const airTemp = weather.air_temp ? `${weather.air_temp.toFixed(1)}\u00B0C` : 'N/A';
    const weatherInfo = weather.skies ? `${weatherIcon} ${escapeHtml(weather.skies)}, Track: ${trackTemp}, Air: ${airTemp}` : 'No weather data';

    container.html(`
        <div class="lap-detail-card">
            <div class="lap-detail-header">
                <h3>Lap #${lap.lap_number || '?'} - ${escapeHtml(lap.track)} - ${lapTimeFormatted}</h3>
            </div>
            <div class="lap-detail-info">
                <span>Car: ${escapeHtml(lap.car_name || lap.car_id || 'Unknown')}</span>
                <span>Data Points: ${lap.data_points || 0}</span>
                <span>Recorded: ${formatDate(lap.session_time || lap.added)}</span>
                ${ownerInfo}
            </div>
            <div class="lap-detail-weather">
                <span class="weather-label">${ICONS.thermometer} Conditions:</span>
                <span class="weather-value">${weatherInfo}</span>
            </div>
            <div class="lap-detail-actions">
                <button type="button" class="btn-primary" onclick="analyzeLap('${lap.id}')" ${teamDisabled}>
                    ${ICONS.search} Analyze Lap
                </button>
                <button type="button" class="btn-secondary" onclick="showSetupPicker('${lap.id}')" ${teamDisabled}>
                    ${ICONS.folder} Select Setup
                </button>
                <button type="button" class="btn-secondary" onclick="getRecommendations('${lap.id}')" ${teamDisabled || (!lap.analysis ? 'disabled title="Analyze lap first"' : '')}>
                    ${ICONS.wrench} Setup Recommendations
                </button>
                <button type="button" class="btn-secondary" onclick="showDrivingRecommendations('${lap.id}')" ${teamDisabled || (!hasDrivingRecs ? 'disabled title="Analyze lap first"' : '')}>
                    ${ICONS.car} Driving Tips
                </button>
            </div>
            <div class="lap-replay" id="lapReplayContainer">
                <div class="lap-replay-header">
                    <h4>${ICONS.video} Lap Replay</h4>
                    <div class="lap-replay-meta" id="lapReplayMeta">Preparing telemetry\u2026</div>
                </div>
                <div class="lap-replay-stats" id="lapReplayStats"></div>
                <div class="lap-replay-body">
                    <canvas id="lapReplayCanvas" width="720" height="400"></canvas>
                    <div class="lap-replay-controls">
                        <button type="button" class="btn-secondary" id="lapReplayToggle">&#9654; Play</button>
                        <input type="range" id="lapReplaySlider" min="0" max="0" value="0">
                        <div class="lap-replay-time" id="lapReplayTime">0.0s</div>
                    </div>
                    <div class="lap-replay-note" id="lapReplayNote">Approximate map derived from telemetry (official centerline can be added later).</div>
                </div>
            </div>
            <div class="lap-telemetry" id="lapTelemetryContainer">
                <div id="lapTelemetryReact"></div>
            </div>
            ${setupHtml}
            ${analysisHtml}
            <div id="lapRecommendations"></div>
            <div id="lapDrivingRecommendations"></div>
        </div>
    `);

    container.show();

    const analyzerLink = document.getElementById('openTelemetryAnalyzer');
    if (analyzerLink) {
        analyzerLink.addEventListener('click', (e: Event) => {
            e.preventDefault();
            const lapIdLocal = lap.id || '';
            window.location.href = `/telemetry?laps=${encodeURIComponent(lapIdLocal)}`;
        });
    }

    initLapReplay(lap);
}

async function analyzeLap(lapId: string): Promise<void> {
    const analyzeBtn = $(`button:contains("Analyze Lap")`);
    analyzeBtn.prop('disabled', true).text('Analyzing...');

    try {
        const lapResponse = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'GET' }));
        const lapData = await lapResponse.json();

        if (!lapData.success || !lapData.lap || !lapData.lap.csv_data) {
            alert('No telemetry data available for this lap');
            analyzeBtn.prop('disabled', false).html(ICONS.search + ' Analyze Lap');
            return;
        }

        const formData = new FormData();
        formData.append('csv_data', lapData.lap.csv_data);

        const analysisResponse = await fetch(apiUrl('analyze_telemetry_api'), authFetchOptions({
            method: 'POST',
            body: formData
        }));

        const analysis = await analysisResponse.json();

        if (analysis.error) {
            alert('Analysis failed: ' + analysis.error);
            analyzeBtn.prop('disabled', false).html(ICONS.search + ' Analyze Lap');
            return;
        }

        const analysisData = {
            issues: analysis.issues?.all || analysis.issues || [],
            issue_keys: analysis.issue_keys || [],
            summary: analysis.summary || {},
            driving_recommendations: analysis.driving_recommendations || null,
            top_setup_recommendations: analysis.top_setup_recommendations || []
        };

        await fetch(apiUrl('/api/laps?action=store_analysis'), authFetchOptions({
            method: 'POST',
            body: JSON.stringify({ lap_id: lapId, analysis: analysis })
        }));

        await loadLaps();
        await selectLap(lapId);

        let coachingImportResult: CoachingImportResult | null = null;
        coachingImportResult = await populateCoachingFromAnalyzedLap(lapId);

        const issueCount = analysisData.issues.length;
        const cornerCount = analysisData.driving_recommendations?.corners_with_issues || 0;
        let message = `Analysis complete!\n\nIssues detected: ${issueCount}\nCorners with driving tips: ${cornerCount}`;

        if (coachingImportResult && coachingImportResult.success) {
            message += `\n\n` + ICONS.target + ` Coaching: ${coachingImportResult.issuesImported} issues imported`;
            if (coachingImportResult.patterns && coachingImportResult.patterns > 0) {
                message += ` (${coachingImportResult.patterns} patterns)`;
            }
        }

        alert(message);

        if (analysisData.driving_recommendations && analysisData.driving_recommendations.corners_with_issues > 0) {
            showDrivingRecommendations(lapId);
        }

    } catch (error: unknown) {
        console.error('Error analyzing lap:', error);
        alert('Error analyzing lap: ' + (error instanceof Error ? error.message : String(error)));
    }

    analyzeBtn.prop('disabled', false).html(ICONS.search + ' Analyze Lap');
}

// ---------------------------------------------------------------------------
// Setup picker / link
// ---------------------------------------------------------------------------

async function showSetupPicker(lapId: string): Promise<void> {
    try {
        const response = await fetch(apiUrl('/api/setups'), authFetchOptions());
        const data = await response.json();
        if (data.success) {
            setupsForPicker = data.setups || [];
        }
    } catch (error: unknown) {
        console.error('Error loading setups:', error);
        setupsForPicker = [];
    }

    const localSetups: SetupItem[] = JSON.parse(localStorage.getItem('setupsDatabase') || '[]');
    setupsForPicker = [...setupsForPicker, ...localSetups];

    const lap = lapsData.find((l) => l.id === lapId);
    const lapTrack = lap?.track?.toLowerCase() || '';

    const matchingSetups = setupsForPicker.filter((s) => {
        const setupTrack = (s.track || '').toLowerCase();
        return setupTrack.includes(lapTrack) || lapTrack.includes(setupTrack) || !lapTrack;
    });

    const otherSetups = setupsForPicker.filter((s) => {
        const setupTrack = (s.track || '').toLowerCase();
        return !setupTrack.includes(lapTrack) && !lapTrack.includes(setupTrack) && lapTrack;
    });

    let setupsHtml = '';

    if (matchingSetups.length > 0) {
        setupsHtml += '<h4>Matching Track</h4>';
        matchingSetups.forEach((setup) => {
            setupsHtml += `
                <div class="setup-picker-item" onclick="linkSetup('${lapId}', '${setup.id}')">
                    <strong>${escapeHtml(setup.setup_name || setup.name || 'Unnamed')}</strong>
                    <span>${escapeHtml(setup.track || 'Unknown Track')}</span>
                </div>
            `;
        });
    }

    if (otherSetups.length > 0) {
        setupsHtml += '<h4>Other Setups</h4>';
        otherSetups.forEach((setup) => {
            setupsHtml += `
                <div class="setup-picker-item" onclick="linkSetup('${lapId}', '${setup.id}')">
                    <strong>${escapeHtml(setup.setup_name || setup.name || 'Unnamed')}</strong>
                    <span>${escapeHtml(setup.track || 'Unknown Track')}</span>
                </div>
            `;
        });
    }

    if (setupsForPicker.length === 0) {
        setupsHtml = '<p class="empty-state">No setups available. Upload a setup first.</p>';
    }

    $('#setupPickerContent').html(setupsHtml);
    $('#setupPickerModal').addClass('active');
}

function closeSetupPicker(): void {
    $('#setupPickerModal').removeClass('active');
}

async function linkSetup(lapId: string, setupId: string): Promise<void> {
    try {
        const response = await fetch(apiUrl('/api/laps?action=link_setup'), authFetchOptions({
            method: 'POST',
            body: JSON.stringify({ lap_id: lapId, setup_id: setupId })
        }));

        const data = await response.json();

        if (data.success) {
            closeSetupPicker();
            await loadLaps();
            await selectLap(lapId);
        } else {
            alert('Failed to link setup: ' + data.message);
        }
    } catch (error: unknown) {
        console.error('Error linking setup:', error);
        alert('Error linking setup: ' + (error instanceof Error ? error.message : String(error)));
    }
}

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

async function getRecommendations(lapId: string): Promise<void> {
    const lap = lapsData.find((l) => l.id === lapId);
    if (!lap) { alert('Lap not found'); return; }

    const lapResponse = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'GET' }));
    const lapDataResp = await lapResponse.json();
    const fullLap: LapData = lapDataResp.lap;

    if (!fullLap?.analysis?.issue_keys || fullLap.analysis.issue_keys.length === 0) {
        alert('Please analyze the lap first to detect issues');
        return;
    }

    let setupData: Record<string, unknown> = {};
    if (fullLap.linked_setup_id) {
        const setup = setupsForPicker.find((s) => s.id === fullLap.linked_setup_id);
        if (setup?.settings) {
            setupData = setup.settings;
        }
    }
    setupData.car_id = fullLap.car_id || 'porsche_911_gt3_r';

    try {
        const requestBody = {
            car: fullLap.car_id || 'porsche_911_gt3_r',
            issues: fullLap.analysis.issue_keys,
            setup: setupData,
            track: fullLap.track
        };

        const response = await fetch(apiUrl('/api/get_recommendations'), authFetchOptions({
            method: 'POST',
            body: JSON.stringify(requestBody)
        }));

        const data = await response.json();

        if (data.success && data.recommendations && data.recommendations.length > 0) {
            displayLapRecommendations(data.recommendations, fullLap, data.track_info);
        } else {
            $('#lapRecommendations').html('<p>No recommendations available for the detected issues.</p>');
        }
    } catch (error: unknown) {
        console.error('Error getting recommendations:', error);
        alert('Error getting recommendations: ' + (error instanceof Error ? error.message : String(error)));
    }
}

function displayLapRecommendations(recommendations: Recommendation[], lap: LapData, trackInfo: TrackInfo | null): void {
    let trackInfoHtml = '';
    if (trackInfo) {
        const trackClass = trackInfo.matched ? 'track-matched' : 'track-unmatched';
        trackInfoHtml = `
            <div class="track-info ${trackClass}">
                <span class="track-name">${ICONS.flag} ${escapeHtml(trackInfo.name)}</span>
                ${trackInfo.characteristics && trackInfo.characteristics.length > 0 ?
                    `<span class="track-chars">${trackInfo.characteristics.slice(0, 3).join(', ')}</span>` : ''}
            </div>
        `;
    }

    let html = `
        <div class="lap-recommendations">
            <h4>Setup Recommendations</h4>
            ${trackInfoHtml}
            ${lap.linked_setup_id ? `<p class="recommendations-setup-info">Based on linked setup</p>` : ''}
    `;

    recommendations.forEach((rec) => {
        const priorityClass = `priority-${rec.priority || 'medium'}`;

        let trackTipsHtml = '';
        if (rec.track_tips && Object.keys(rec.track_tips).length > 0) {
            const tips = Object.values(rec.track_tips);
            trackTipsHtml = `
                <div class="track-tips">
                    <span class="track-tip-icon">${ICONS.flag}</span>
                    <span class="track-tip-text">${escapeHtml(tips[0])}</span>
                </div>
            `;
        }

        let valueHtml = '';
        if (rec.current_value && rec.suggested_value) {
            valueHtml = `
                <p class="value-change">
                    <span class="current">${escapeHtml(rec.current_value)}</span>
                    <span class="arrow">\u2192</span>
                    <span class="suggested">${escapeHtml(rec.suggested_value)}</span>
                    ${rec.change ? `<span class="change">(${escapeHtml(rec.change)})</span>` : ''}
                </p>
            `;
        } else {
            valueHtml = `
                <p class="suggested-value">
                    ${rec.direction ? `${escapeHtml(rec.direction)} ` : ''}${escapeHtml(rec.amount || '')}
                </p>
            `;
        }

        let addressesHtml = '';
        if (rec.multi_issue && rec.addresses) {
            addressesHtml = `<p class="addresses">Addresses: ${escapeHtml(rec.addresses)}</p>`;
        }

        html += `
            <div class="recommendation-item ${priorityClass}">
                <h4>
                    ${escapeHtml(rec.setting || rec.title || '')}
                    <span class="priority-badge">${rec.priority || 'medium'}</span>
                </h4>
                ${valueHtml}
                <p class="explanation">${escapeHtml(rec.explanation || '')}</p>
                ${trackTipsHtml}
                ${addressesHtml}
            </div>
        `;
    });

    html += '</div>';
    $('#lapRecommendations').html(html);
}

// ---------------------------------------------------------------------------
// Driving recommendations
// ---------------------------------------------------------------------------

async function showDrivingRecommendations(lapId: string): Promise<void> {
    const lap = lapsData.find((l) => l.id === lapId);
    if (!lap) { alert('Lap not found'); return; }

    const lapResponse = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'GET' }));
    const lapDataResp = await lapResponse.json();
    const fullLap: LapData = lapDataResp.lap;

    if (!fullLap?.analysis?.driving_recommendations) {
        alert('No driving recommendations available. Please analyze the lap first.');
        return;
    }

    displayDrivingRecommendations(fullLap.analysis.driving_recommendations);
}

function displayDrivingRecommendations(drivingRecs: DrivingRecommendations): void {
    const container = $('#lapDrivingRecommendations');

    if (!drivingRecs || !drivingRecs.corners || drivingRecs.corners.length === 0) {
        container.html('<p class="no-recommendations">No corner-specific driving issues detected.</p>');
        return;
    }

    let html = `
        <div class="driving-recommendations">
            <h4>${ICONS.car} Driving Improvement Tips</h4>
            <div class="driving-summary">
                <p><strong>Corners Analyzed:</strong> ${drivingRecs.total_corners_analyzed}</p>
                <p><strong>Corners with Issues:</strong> ${drivingRecs.corners_with_issues}</p>
    `;

    if (drivingRecs.summary) {
        const summary = drivingRecs.summary;
        if (summary.focus_areas && summary.focus_areas.length > 0) {
            html += `
                <div class="focus-areas">
                    <h5>Focus Areas:</h5>
                    <ul>${summary.focus_areas.map((area) => `<li>${escapeHtml(area)}</li>`).join('')}</ul>
                </div>
            `;
        }
        if (summary.general_advice) {
            html += `<p class="general-advice"><strong>${ICONS.lightbulb} Tip:</strong> ${escapeHtml(summary.general_advice)}</p>`;
        }
        if (summary.most_problematic_corners && summary.most_problematic_corners.length > 0) {
            html += `
                <div class="problematic-corners">
                    <h5>Top Corners to Improve:</h5>
                    <ul>
                        ${summary.most_problematic_corners.slice(0, 3).map((c) =>
                            `<li>Corner ${c.corner} (${c.location_pct.toFixed(1)}% of lap) - ${c.issue_count} issues</li>`
                        ).join('')}
                    </ul>
                </div>
            `;
        }
    }

    html += `</div>`;

    html += `<div class="corner-breakdown"><h5>Corner-by-Corner Breakdown</h5><div class="corner-accordion">`;

    drivingRecs.corners.forEach((corner) => {
        const location = corner.location;
        const directionIcon = location.direction === 'left' ? '\u21B0' : '\u21B1';
        const speedClass = `speed-${location.speed_category}`;

        html += `
            <div class="corner-item ${speedClass}">
                <div class="corner-header" onclick="toggleCornerDetails(this)">
                    <span class="corner-number">Corner ${corner.corner_number}</span>
                    <span class="corner-info">
                        ${directionIcon} ${escapeHtml(location.speed_category)}
                        (${location.min_speed.toFixed(0)} mph)
                        @ ${location.start_pct.toFixed(1)}%
                    </span>
                    ${corner.priority_focus ? `<span class="priority-issue">${escapeHtml(corner.priority_focus.issue)}</span>` : ''}
                    <span class="expand-icon">\u25BC</span>
                </div>
                <div class="corner-details" style="display: none;">
        `;

        if (corner.issues_found && corner.issues_found.length > 0) {
            html += `<div class="corner-issues"><h6>Issues Detected:</h6><ul>
                ${corner.issues_found.map((issue) => `
                    <li class="issue-${issue.phase}">
                        <strong>${escapeHtml(issue.name)}</strong>
                        <span class="issue-phase">[${escapeHtml(issue.phase)}]</span>
                        <span class="issue-severity">${issue.severity}%</span>
                        <p class="issue-evidence">${escapeHtml(issue.evidence)}</p>
                    </li>
                `).join('')}
            </ul></div>`;
        }

        const recs = corner.recommendations;
        if (recs.entry && recs.entry.length > 0) {
            html += `<div class="phase-recs phase-entry"><h6>${ICONS.circleDot} Entry:</h6><ul>
                ${recs.entry.slice(0, 2).map((r) => `<li class="${r.priority === 'high' ? 'high-priority' : ''}">${r.priority === 'high' ? '\u2605 ' : ''}${escapeHtml(r.advice)}</li>`).join('')}
            </ul></div>`;
        }
        if (recs.mid && recs.mid.length > 0) {
            html += `<div class="phase-recs phase-mid"><h6>${ICONS.refreshCw} Mid-Corner:</h6><ul>
                ${recs.mid.slice(0, 2).map((r) => `<li class="${r.priority === 'high' ? 'high-priority' : ''}">${r.priority === 'high' ? '\u2605 ' : ''}${escapeHtml(r.advice)}</li>`).join('')}
            </ul></div>`;
        }
        if (recs.exit && recs.exit.length > 0) {
            html += `<div class="phase-recs phase-exit"><h6>${ICONS.flag} Exit:</h6><ul>
                ${recs.exit.slice(0, 2).map((r) => `<li class="${r.priority === 'high' ? 'high-priority' : ''}">${r.priority === 'high' ? '\u2605 ' : ''}${escapeHtml(r.advice)}</li>`).join('')}
            </ul></div>`;
        }
        if (recs.general && recs.general.length > 0) {
            html += `<div class="phase-recs phase-general"><h6>${ICONS.lightbulb} Technique Tip:</h6><p>${escapeHtml(recs.general[0].advice)}</p></div>`;
        }

        html += `</div></div>`;
    });

    html += `</div></div></div>`;
    container.html(html);
}

function toggleCornerDetails(headerElement: HTMLElement): void {
    const details = $(headerElement).siblings('.corner-details');
    const icon = $(headerElement).find('.expand-icon');
    details.slideToggle(200);
    icon.text(details.is(':visible') ? '\u25B2' : '\u25BC');
}

// ============= LAP REPLAY =============

async function initLapReplay(lap: LapData): Promise<void> {
    stopLapReplay();

    const canvas = document.getElementById('lapReplayCanvas') as HTMLCanvasElement | null;
    const toggleBtn = document.getElementById('lapReplayToggle') as HTMLButtonElement | null;
    const slider = document.getElementById('lapReplaySlider') as HTMLInputElement | null;
    const timeLabel = document.getElementById('lapReplayTime');
    const meta = document.getElementById('lapReplayMeta');
    const note = document.getElementById('lapReplayNote');

    if (!canvas || !toggleBtn || !slider || !timeLabel || !meta) return;

    resizeReplayCanvas(canvas);

    if (!lap.csv_data) {
        meta.textContent = 'No telemetry data available for replay.';
        toggleBtn.disabled = true;
        slider.disabled = true;
        if (note) note.textContent = '';
        return;
    }

    const telemetry = parseTelemetryCsv(lap.csv_data);
    if (!telemetry.points.length) {
        meta.textContent = 'Telemetry data missing required columns.';
        toggleBtn.disabled = true;
        slider.disabled = true;
        if (note) note.textContent = '';
        return;
    }

    const trackMap = await loadTrackMap(lap.track);
    const replayPath = buildReplayPath(telemetry.points, trackMap ? trackMap.points : null);
    if (!replayPath.points.length) {
        meta.textContent = 'Could not reconstruct track map.';
        toggleBtn.disabled = true;
        slider.disabled = true;
        if (note) note.textContent = '';
        return;
    }

    lapReplayState.canvas = canvas;
    lapReplayState.ctx = canvas.getContext('2d');
    lapReplayState.points = replayPath.points;
    lapReplayState.sampleTimes = replayPath.sampleTimes;
    lapReplayState.baseMapPoints = replayPath.baseMapPoints;
    lapReplayState.mapType = replayPath.mapType;
    lapReplayState.telemetrySeries = telemetry.points;
    lapReplayState.cornerLabels = trackMap ? trackMap.cornerLabels : [];
    lapReplayState.visibleTelemetrySeries = [];
    lapReplayState.coachMessages = buildCoachMessages(lap);
    lapReplayState.coachIndex = 0;
    lapReplayState.currentIndex = 0;
    lapReplayState.elapsedTime = 0;
    lapReplayState.zoom = 1;
    lapReplayState.panX = 0;
    lapReplayState.panY = 0;
    miniMapState.dismissed = false;

    if (lapReplayState.baseMapPoints.length > 10) {
        const osmWidth = trackMap?.osmData?.avg_width_meters || null;
        const ribbon = generateSyntheticRibbon(lapReplayState.baseMapPoints, osmWidth);
        let features: OsmFeature[] | null = null;
        if (trackMap?.osmData) {
            const transform = procrustesAlign(trackMap.osmData.centerline, lapReplayState.baseMapPoints);
            if (transform) {
                features = trackMap.osmData.features.map((f) => {
                    const result: OsmFeature = { type: f.type, name: f.name };
                    if (f.polygon) result.polygon = applyTransformPoints(f.polygon, transform);
                    if (f.center) { result.center = applyTransformPoint(f.center, transform); result.radius = (f.radius || 3) * transform.scale; }
                    return result;
                });
                console.log('[OSM] Features aligned:', features.length, 'items. Width hint:', osmWidth);
            }
        }
        lapReplayState.osmAligned = { ribbon, features: features || [] };
        console.log('[OSM] Ribbon from centerline.', trackMap?.osmData ? 'OSM features: ' + (features?.length || 0) : 'No OSM data.');
    } else {
        lapReplayState.osmAligned = null;
        console.log('[OSM] No base map points for ribbon.');
    }

    resizeReplayCanvas(canvas, lapReplayState.baseMapPoints);
    setupReplayResizeWatcher(canvas);
    applyReplayScaling(canvas, lapReplayState.baseMapPoints);
    setupReplayZoomPan(canvas);

    meta.textContent = `Samples: ${replayPath.points.length} \u00B7 Duration: ${replayPath.duration.toFixed(1)}s`;
    slider.max = String(Math.max(0, replayPath.points.length - 1));
    slider.value = '0';
    slider.disabled = false;
    toggleBtn.disabled = false;
    toggleBtn.innerHTML = '&#9654; Play';
    if (note) {
        note.textContent = lapReplayState.mapType === 'official'
            ? 'Official track layout (Long Beach SVG) with telemetry replay.'
            : 'Approximate map derived from telemetry (official centerline can be added later).';
    }

    drawLapReplay();

    if (lapReplayState._telemetryHandle) {
        lapReplayState._telemetryHandle.unmount();
    }
    if (window.__mountTelemetryChart) {
        lapReplayState._telemetryHandle = window.__mountTelemetryChart('lapTelemetryReact', [lap.id]);
    }

    setupCoachFeed();
    timeLabel.textContent = '0.0s';

    setupMiniMap();
    updateMiniMapVisibility();

    toggleBtn.onclick = (): void => {
        if (lapReplayState.playing) {
            stopLapReplay();
        } else {
            startLapReplay();
        }
    };

    slider.oninput = (event: Event): void => {
        const index = parseInt((event.target as HTMLInputElement).value, 10);
        setLapReplayIndex(index);
    };
}

function resizeReplayCanvas(canvas: HTMLCanvasElement, trackPoints?: MapPoint[]): void {
    const deviceRatio = window.devicePixelRatio || 1;
    const parentWidth = canvas.parentElement ? canvas.parentElement.getBoundingClientRect().width : 0;
    const canvasWidth = canvas.getBoundingClientRect().width;
    const width = Math.max(320, Math.round(parentWidth || canvasWidth || 720));

    let height: number;
    const padding = 30;
    if (trackPoints && trackPoints.length > 10) {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const p of trackPoints) {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        }
        const spanX = Math.max(1, maxX - minX);
        const spanY = Math.max(1, maxY - minY);
        const trackRatio = spanY / spanX;
        const drawWidth = width - padding * 2;
        const idealHeight = Math.round(drawWidth * trackRatio + padding * 2);
        const maxHeight = Math.round(window.innerHeight * 0.75);
        height = Math.min(maxHeight, Math.max(280, idealHeight));
    } else {
        const maxHeight = Math.max(380, Math.round(window.innerHeight * 0.7));
        const idealHeight = Math.round(width * 0.55);
        height = Math.min(maxHeight, Math.max(400, idealHeight));
    }

    canvas.style.width = '100%';
    canvas.style.height = `${height}px`;
    canvas.width = Math.round(width * deviceRatio);
    canvas.height = Math.round(height * deviceRatio);

    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);
    }
}

function setupReplayResizeWatcher(canvas: HTMLCanvasElement): void {
    if (!canvas) return;

    if (lapReplayState.resizeObserver) {
        lapReplayState.resizeObserver.disconnect();
        lapReplayState.resizeObserver = null;
    }
    if (lapReplayState.resizeHandler) {
        window.removeEventListener('resize', lapReplayState.resizeHandler);
        lapReplayState.resizeHandler = null;
    }

    const handleResize = (): void => {
        const mapPoints = lapReplayState.baseMapPoints.length ? lapReplayState.baseMapPoints : lapReplayState.points;
        resizeReplayCanvas(canvas, mapPoints.length > 10 ? mapPoints : undefined);
        if (mapPoints.length) {
            applyReplayScaling(canvas, mapPoints);
        }
        drawLapReplay();
    };

    const debounced = (): void => {
        if (lapReplayState.resizeTimeoutId) {
            clearTimeout(lapReplayState.resizeTimeoutId);
        }
        lapReplayState.resizeTimeoutId = setTimeout(handleResize, 80);
    };

    lapReplayState.resizeHandler = debounced;
    window.addEventListener('resize', debounced);

    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(debounced);
        observer.observe(canvas.parentElement || canvas);
        lapReplayState.resizeObserver = observer;
    }

    requestAnimationFrame(debounced);
}

function parseTelemetryCsv(csvData: string): { points: TelemetryPoint[] } {
    const lines = csvData.trim().split(/\r\n|\r|\n/);
    if (lines.length < 2) return { points: [] };

    const header = lines[0].split(',').map((name) => name.trim());
    const columnIndex = (name: string): number => header.indexOf(name);

    const idxSessionTime = columnIndex('SessionTime');
    const idxSpeed = columnIndex('Speed');
    const idxYawRate = columnIndex('YawRate');
    const idxLatAccel = columnIndex('LatAccel');
    const idxLongAccel = columnIndex('LongAccel');
    const idxLapDist = columnIndex('LapDistPct');
    const idxThrottle = columnIndex('Throttle');
    const idxBrake = columnIndex('Brake');
    const idxClutch = columnIndex('Clutch');
    const idxSteering = columnIndex('SteeringWheelAngle');
    const idxVertAccel = columnIndex('VertAccel');
    const idxGear = columnIndex('Gear');
    const idxRpm = columnIndex('RPM');
    const idxAbs = columnIndex('ABSActive');
    const idxLatitude = columnIndex('Latitude');
    const idxLongitude = columnIndex('Longitude');
    const idxAltitude = columnIndex('Altitude');
    const idxYaw = columnIndex('Yaw');
    const idxPitch = columnIndex('Pitch');
    const idxPitchRate = columnIndex('PitchRate');
    const idxRoll = columnIndex('Roll');
    const idxRollRate = columnIndex('RollRate');
    const idxTcActive = columnIndex('TCActive');
    const idxTc2Active = columnIndex('TC2Active');
    const idxLFtempCL = columnIndex('LFtempCL');
    const idxLFtempCM = columnIndex('LFtempCM');
    const idxLFtempCR = columnIndex('LFtempCR');
    const idxRFtempCL = columnIndex('RFtempCL');
    const idxRFtempCM = columnIndex('RFtempCM');
    const idxRFtempCR = columnIndex('RFtempCR');
    const idxLRtempCL = columnIndex('LRtempCL');
    const idxLRtempCM = columnIndex('LRtempCM');
    const idxLRtempCR = columnIndex('LRtempCR');
    const idxRRtempCL = columnIndex('RRtempCL');
    const idxRRtempCM = columnIndex('RRtempCM');
    const idxRRtempCR = columnIndex('RRtempCR');
    const idxLFpressure = columnIndex('LFpressure');
    const idxRFpressure = columnIndex('RFpressure');
    const idxLRpressure = columnIndex('LRpressure');
    const idxRRpressure = columnIndex('RRpressure');
    const idxLFspeed = columnIndex('LFspeed');
    const idxRFspeed = columnIndex('RFspeed');
    const idxLRspeed = columnIndex('LRspeed');
    const idxRRspeed = columnIndex('RRspeed');
    const idxLFshockDefl = columnIndex('LFshockDefl');
    const idxRFshockDefl = columnIndex('RFshockDefl');
    const idxLRshockDefl = columnIndex('LRshockDefl');
    const idxRRshockDefl = columnIndex('RRshockDefl');
    const idxWaterTemp = columnIndex('WaterTemp');
    const idxOilTemp = columnIndex('OilTemp');
    const idxOilPress = columnIndex('OilPress');
    const idxVoltage = columnIndex('Voltage');

    if ([idxSessionTime, idxSpeed, idxYawRate].some((idx) => idx === -1)) {
        return { points: [] };
    }

    const parseValue = (idx: number, row: string[], defaultVal: number = 0): number => {
        if (idx < 0) return defaultVal;
        const val = parseFloat(row[idx]);
        return Number.isNaN(val) ? defaultVal : val;
    };

    const parseBool = (idx: number, row: string[]): number => {
        if (idx < 0) return 0;
        const val = String(row[idx]).trim().toLowerCase();
        return val === 'true' || val === '1' ? 1 : 0;
    };

    const points: TelemetryPoint[] = [];
    for (let i = 1; i < lines.length; i++) {
        const row = lines[i].trim();
        if (!row) continue;
        const cols = row.split(',');

        const sessionTime = parseFloat(cols[idxSessionTime]);
        const speed = parseFloat(cols[idxSpeed]);
        const yawRate = parseFloat(cols[idxYawRate]);

        if (Number.isNaN(sessionTime) || Number.isNaN(speed) || Number.isNaN(yawRate)) continue;

        points.push({
            sessionTime, speed, yawRate,
            latAccel: parseValue(idxLatAccel, cols),
            longAccel: parseValue(idxLongAccel, cols),
            vertAccel: parseValue(idxVertAccel, cols),
            lapDistPct: parseValue(idxLapDist, cols),
            throttle: parseValue(idxThrottle, cols),
            brake: parseValue(idxBrake, cols),
            clutch: parseValue(idxClutch, cols),
            steering: parseValue(idxSteering, cols),
            gear: parseValue(idxGear, cols),
            rpm: parseValue(idxRpm, cols),
            absActive: parseBool(idxAbs, cols),
            latitude: parseValue(idxLatitude, cols),
            longitude: parseValue(idxLongitude, cols),
            altitude: parseValue(idxAltitude, cols),
            yaw: parseValue(idxYaw, cols),
            pitch: parseValue(idxPitch, cols),
            pitchRate: parseValue(idxPitchRate, cols),
            roll: parseValue(idxRoll, cols),
            rollRate: parseValue(idxRollRate, cols),
            tcActive: parseValue(idxTcActive, cols),
            tc2Active: parseValue(idxTc2Active, cols),
            LFtempCL: parseValue(idxLFtempCL, cols),
            LFtempCM: parseValue(idxLFtempCM, cols),
            LFtempCR: parseValue(idxLFtempCR, cols),
            RFtempCL: parseValue(idxRFtempCL, cols),
            RFtempCM: parseValue(idxRFtempCM, cols),
            RFtempCR: parseValue(idxRFtempCR, cols),
            LRtempCL: parseValue(idxLRtempCL, cols),
            LRtempCM: parseValue(idxLRtempCM, cols),
            LRtempCR: parseValue(idxLRtempCR, cols),
            RRtempCL: parseValue(idxRRtempCL, cols),
            RRtempCM: parseValue(idxRRtempCM, cols),
            RRtempCR: parseValue(idxRRtempCR, cols),
            LFpressure: parseValue(idxLFpressure, cols),
            RFpressure: parseValue(idxRFpressure, cols),
            LRpressure: parseValue(idxLRpressure, cols),
            RRpressure: parseValue(idxRRpressure, cols),
            LFspeed: parseValue(idxLFspeed, cols),
            RFspeed: parseValue(idxRFspeed, cols),
            LRspeed: parseValue(idxLRspeed, cols),
            RRspeed: parseValue(idxRRspeed, cols),
            LFshockDefl: parseValue(idxLFshockDefl, cols),
            RFshockDefl: parseValue(idxRFshockDefl, cols),
            LRshockDefl: parseValue(idxLRshockDefl, cols),
            RRshockDefl: parseValue(idxRRshockDefl, cols),
            waterTemp: parseValue(idxWaterTemp, cols),
            oilTemp: parseValue(idxOilTemp, cols),
            oilPress: parseValue(idxOilPress, cols),
            voltage: parseValue(idxVoltage, cols)
        });
    }

    return { points };
}

// ---------------------------------------------------------------------------
// Track map alignment & replay path building
// ---------------------------------------------------------------------------

function alignTrackMapToTelemetry(trackMapPoints: MapPoint[], telemetryPoints: TelemetryPoint[]): MapPoint[] {
    const n = trackMapPoints.length;
    if (n < 20) return trackMapPoints;

    const telPcts = telemetryPoints.map((p) => normalizeLapDistPct(p.lapDistPct));
    const telYaw: number[] = [];
    for (let i = 0; i < telemetryPoints.length; i++) {
        const prev = i > 0 ? telemetryPoints[i - 1] : null;
        const dt = prev ? Math.max(0.001, telemetryPoints[i].sessionTime - prev.sessionTime) : 1 / 60;
        telYaw.push(telemetryPoints[i].yawRate * dt);
    }

    const telCurv = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const targetPct = i / (n - 1);
        let lo = 0;
        for (let j = 0; j < telPcts.length - 1; j++) {
            if (telPcts[j] <= targetPct && telPcts[j + 1] >= targetPct) {
                lo = j; break;
            }
        }
        telCurv[i] = telYaw[lo];
    }

    const svgCurv = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const prev = trackMapPoints[(i - 1 + n) % n];
        const cur = trackMapPoints[i];
        const next = trackMapPoints[(i + 1) % n];
        const a1 = Math.atan2(cur.y - prev.y, cur.x - prev.x);
        const a2 = Math.atan2(next.y - cur.y, next.x - cur.x);
        let da = a2 - a1;
        while (da > Math.PI) da -= 2 * Math.PI;
        while (da < -Math.PI) da += 2 * Math.PI;
        svgCurv[i] = da;
    }

    const step = Math.max(1, Math.floor(n / 150));
    let bestScore = -Infinity, bestOffset = 0;

    for (let offset = 0; offset < n; offset += step) {
        let score = 0;
        for (let i = 0; i < n; i += step) {
            const svgIdx = (offset + i) % n;
            score += telCurv[i] * svgCurv[svgIdx];
        }
        if (score > bestScore) { bestScore = score; bestOffset = offset; }
    }

    const refineStart = Math.max(0, bestOffset - step);
    const refineEnd = Math.min(n, bestOffset + step);
    for (let offset = refineStart; offset < refineEnd; offset++) {
        let score = 0;
        for (let i = 0; i < n; i += step) {
            const svgIdx = (offset + i) % n;
            score += telCurv[i] * svgCurv[svgIdx];
        }
        if (score > bestScore) { bestScore = score; bestOffset = offset; }
    }

    const aligned: MapPoint[] = [];
    for (let i = 0; i < n; i++) {
        aligned.push(trackMapPoints[(bestOffset + i) % n]);
    }
    return aligned;
}

function buildReplayPath(points: TelemetryPoint[], trackMapPoints: MapPoint[] | null): ReplayPath {
    if (!points.length) {
        return { points: [], sampleTimes: [], duration: 0, baseMapPoints: [], mapType: 'approx' };
    }

    const sampleTimes: number[] = [];
    const startTime = points[0].sessionTime;

    if (trackMapPoints && trackMapPoints.length > 10) {
        const aligned = alignTrackMapToTelemetry(trackMapPoints, points);
        const mappedPoints: MapPoint[] = [];
        for (let i = 0; i < points.length; i++) {
            const pct = normalizeLapDistPct(points[i].lapDistPct);
            const idx = Math.max(0, Math.min(aligned.length - 1, Math.round(pct * (aligned.length - 1))));
            mappedPoints.push(aligned[idx]);
            sampleTimes.push(points[i].sessionTime - startTime);
        }
        const duration = sampleTimes[sampleTimes.length - 1] || 0;
        return { points: mappedPoints, sampleTimes, duration, baseMapPoints: aligned, mapType: 'official' };
    }

    const hasLatLon = points.some((p) => p.latitude && p.longitude &&
        Math.abs(p.latitude) > 0.0001 && Math.abs(p.longitude) > 0.0001);

    if (hasLatLon) {
        const latLonPoints: MapPoint[] = [];
        const refLat = points[0].latitude;
        const refLon = points[0].longitude;
        const latScale = 111320;
        const lonScale = 111320 * Math.cos(refLat * Math.PI / 180);

        for (let i = 0; i < points.length; i++) {
            const x = (points[i].longitude - refLon) * lonScale;
            const y = (points[i].latitude - refLat) * latScale;
            latLonPoints.push({ x, y, lapDistPct: points[i].lapDistPct });
            sampleTimes.push(points[i].sessionTime - startTime);
        }
        const duration = sampleTimes[sampleTimes.length - 1] || 0;
        return { points: latLonPoints, sampleTimes, duration, baseMapPoints: latLonPoints, mapType: 'gps' };
    }

    const rawPath: MapPoint[] = [];
    let heading = 0;
    let x = 0;
    let y = 0;

    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const prev = i > 0 ? points[i - 1] : null;
        const dt = prev ? Math.max(0.001, current.sessionTime - prev.sessionTime) : 1 / 60;
        const speedMps = current.speed * 0.44704;
        heading += current.yawRate * dt;
        x += speedMps * dt * Math.cos(heading);
        y += speedMps * dt * Math.sin(heading);
        rawPath.push({ x, y, lapDistPct: current.lapDistPct });
        sampleTimes.push(current.sessionTime - startTime);
    }

    if (rawPath.length > 1) {
        const last = rawPath[rawPath.length - 1];
        const driftX = last.x;
        const driftY = last.y;
        const n = rawPath.length;
        for (let i = 0; i < n; i++) {
            const t = i / (n - 1);
            rawPath[i].x -= driftX * t;
            rawPath[i].y -= driftY * t;
        }
    }

    const duration = sampleTimes[sampleTimes.length - 1] || 0;
    return { points: rawPath, sampleTimes, duration, baseMapPoints: rawPath, mapType: 'approx' };
}

function normalizeLapDistPct(value: number): number {
    if (!value || Number.isNaN(value)) return 0;
    if (value > 1.1) return Math.max(0, Math.min(1, value / 100));
    return Math.max(0, Math.min(1, value));
}

function normalizeTrackName(name: string): string {
    if (!name) return '';
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

async function loadTrackMapFromSvg(svgUrl: string): Promise<TrackMapData | null> {
    try {
        const response = await fetch(svgUrl);
        if (!response.ok) throw new Error('Track map fetch failed');
        const svgText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const paths = Array.from(doc.querySelectorAll('path'));
        const textNodes = Array.from(doc.querySelectorAll('text'));
        if (!paths.length) return null;

        const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        tempSvg.setAttribute('width', '0');
        tempSvg.setAttribute('height', '0');
        tempSvg.style.position = 'absolute';
        tempSvg.style.left = '-9999px';
        tempSvg.style.top = '-9999px';
        document.body.appendChild(tempSvg);

        const trackPaths = filterTrackPaths(paths);
        const candidates = trackPaths.length ? trackPaths : paths;

        let longest: { path: SVGPathElement; length: number } | null = null;
        candidates.forEach((path) => {
            const clone = path.cloneNode(true) as SVGPathElement;
            tempSvg.appendChild(clone);
            try {
                const length = clone.getTotalLength();
                if (!longest || length > longest.length) {
                    longest = { path: clone, length };
                }
            } catch (_err: unknown) {
                tempSvg.removeChild(clone);
            }
        });

        if (!longest) {
            document.body.removeChild(tempSvg);
            return null;
        }

        const samples = 1200;
        const points: MapPoint[] = [];
        for (let i = 0; i < samples; i++) {
            const point = (longest as { path: SVGPathElement; length: number }).path.getPointAtLength(((longest as { path: SVGPathElement; length: number }).length * i) / (samples - 1));
            points.push({ x: point.x, y: point.y });
        }

        const cornerLabels = extractCornerLabels(textNodes, points);
        document.body.removeChild(tempSvg);
        return { points, cornerLabels };
    } catch (_err: unknown) {
        return null;
    }
}

async function loadTrackMap(trackName: string): Promise<TrackMapData | null> {
    const key = normalizeTrackName(trackName);
    if (trackMapCache.has(key)) return trackMapCache.get(key) || null;

    let map: TrackMapData | null = null;

    const source = trackMapSources[key];
    if (source) {
        const svgMap = await loadTrackMapFromSvg(source);
        if (svgMap) map = svgMap;
    }

    if (!map) {
        try {
            const resp = await fetch(`/api/track-map/${key}`);
            if (resp.ok) {
                const respData = await resp.json();
                if (respData.data?.points?.length > 10) {
                    map = { points: respData.data.points, cornerLabels: [] };
                }
            }
        } catch (_e: unknown) { /* fall through */ }
    }

    try {
        const osmData = await loadOsmData(key);
        console.log(`[OSM] loadOsmData('${key}'):`, osmData ? `${osmData.centerline.length} pts, ${osmData.features.length} features` : 'null');
        if (map && osmData) {
            map.osmData = osmData;
        } else if (!map && osmData) {
            map = { points: osmData.centerline, cornerLabels: [], osmData };
        }
    } catch (e: unknown) { console.warn('[OSM] Error loading OSM data:', e); }

    if (map) {
        trackMapCache.set(key, map);
        return map;
    }
    return null;
}

// ============= OSM TRACK DATA =============

const osmDataCache: Map<string, OsmData | null> = new Map();

async function loadOsmData(trackKey: string): Promise<OsmData | null> {
    if (osmDataCache.has(trackKey)) return osmDataCache.get(trackKey) || null;

    const variants = [trackKey];
    const keyMap: Record<string, string> = {
        circuitdespafrancorchamps: 'spa', spafrancorchamps: 'spa', spa: 'spa',
        autodromonazionalemonza: 'monza', monza: 'monza',
        silverstonecircuit: 'silverstone', silverstone: 'silverstone',
        suzukainternationalracingcourse: 'suzuka', suzuka: 'suzuka',
        circuitdebarcelonacatalunya: 'barcelona', barcelona: 'barcelona',
        nurburgringgrandprixstrecke: 'nurburgring_gp', nurburgring: 'nurburgring_gp',
        brandshatchcircuit: 'brands_hatch', brandshatch: 'brands_hatch',
        autodromointernazionaleenzoedinoferrari: 'imola', imola: 'imola',
        circuitdes24heuresdumans: 'le_mans', lemans: 'le_mans',
        sebringinternationalraceway: 'sebring', sebring: 'sebring',
        watkinsgleninternational: 'watkins_glen', watkinsglen: 'watkins_glen',
        roadatlanta: 'road_atlanta',
        autodromojosecarlospace: 'interlagos', interlagos: 'interlagos',
        circuitparkzandvoort: 'zandvoort', zandvoort: 'zandvoort',
        phillipislandcircuit: 'phillip_island', phillipisland: 'phillip_island', phillipislandgrandprixcircuit: 'phillip_island',
        weathertechracewayatlagunaseca: 'laguna_seca', lagunaseca: 'laguna_seca',
        longbeachstreetcircuit: 'long_beach', longbeach: 'long_beach',
        daytonainternationalspeedwayroadcourse: 'daytona_road', daytonaroadcourse: 'daytona_road',
        roadamerica: 'road_america',
        circuitoftheamericas: 'cota', cota: 'cota',
    };

    const osmKey = keyMap[trackKey];
    if (osmKey) variants.push(osmKey);
    if (!variants.includes(trackKey)) variants.push(trackKey);

    for (const key of variants) {
        try {
            const resp = await fetch(`/assets/tracks/osm/${key}.json`);
            if (resp.ok) {
                const data: OsmData = await resp.json();
                osmDataCache.set(trackKey, data);
                return data;
            }
        } catch (_e: unknown) { /* try next */ }
    }

    osmDataCache.set(trackKey, null);
    return null;
}

function procrustesAlign(osmCenterline: MapPoint[], mapPoints: MapPoint[]): ProcrustesTransform | null {
    if (!osmCenterline || osmCenterline.length < 10 || !mapPoints || mapPoints.length < 10) return null;

    function centroid(pts: MapPoint[]): { x: number; y: number } {
        let cx = 0, cy = 0;
        for (const p of pts) { cx += p.x; cy += p.y; }
        return { x: cx / pts.length, y: cy / pts.length };
    }

    function principalAngle(pts: MapPoint[], c: { x: number; y: number }): number {
        let cxx = 0, cxy = 0, cyy = 0;
        for (const p of pts) {
            const dx = p.x - c.x, dy = p.y - c.y;
            cxx += dx * dx; cxy += dx * dy; cyy += dy * dy;
        }
        return 0.5 * Math.atan2(2 * cxy, cxx - cyy);
    }

    function extentAlongAngle(pts: MapPoint[], c: { x: number; y: number }, angle: number): { width: number; height: number } {
        const cos = Math.cos(angle), sin = Math.sin(angle);
        let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
        for (const p of pts) {
            const dx = p.x - c.x, dy = p.y - c.y;
            const u = dx * cos + dy * sin;
            const v = -dx * sin + dy * cos;
            if (u < minU) minU = u; if (u > maxU) maxU = u;
            if (v < minV) minV = v; if (v > maxV) maxV = v;
        }
        return { width: maxU - minU, height: maxV - minV };
    }

    const osmC = centroid(osmCenterline);
    const mapC = centroid(mapPoints);
    const osmAngle = principalAngle(osmCenterline, osmC);
    const mapAngle = principalAngle(mapPoints, mapC);

    let theta = mapAngle - osmAngle;

    const osmExt = extentAlongAngle(osmCenterline, osmC, osmAngle);
    const mapExt = extentAlongAngle(mapPoints, mapC, mapAngle);

    const candidates = [theta, theta + Math.PI];
    let bestTheta = theta;
    let bestError = Infinity;

    for (const candidateTheta of candidates) {
        const cosT = Math.cos(candidateTheta), sinT = Math.sin(candidateTheta);
        const step = Math.max(1, Math.floor(osmCenterline.length / 20));
        const scaleEst = Math.sqrt((mapExt.width * mapExt.height) / Math.max(osmExt.width * osmExt.height, 1e-10));
        let error = 0;
        for (let i = 0; i < osmCenterline.length; i += step) {
            const dx = osmCenterline[i].x - osmC.x;
            const dy = osmCenterline[i].y - osmC.y;
            const tx = (dx * cosT - dy * sinT) * scaleEst + mapC.x;
            const ty = (dx * sinT + dy * cosT) * scaleEst + mapC.y;
            let minDist = Infinity;
            for (let j = 0; j < mapPoints.length; j += step) {
                const d = (tx - mapPoints[j].x) ** 2 + (ty - mapPoints[j].y) ** 2;
                if (d < minDist) minDist = d;
            }
            error += minDist;
        }
        if (error < bestError) { bestError = error; bestTheta = candidateTheta; }
    }

    theta = bestTheta;
    const scale = Math.sqrt((mapExt.width * mapExt.height) / Math.max(osmExt.width * osmExt.height, 1e-10));
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);

    console.log(`[OSM] PCA alignment: osmAngle=${(osmAngle * 180 / Math.PI).toFixed(1)}\u00B0 mapAngle=${(mapAngle * 180 / Math.PI).toFixed(1)}\u00B0 theta=${(theta * 180 / Math.PI).toFixed(1)}\u00B0 scale=${scale.toFixed(4)}`);

    return { scale, cosT, sinT, osmCx: osmC.x, osmCy: osmC.y, mapCx: mapC.x, mapCy: mapC.y };
}

function applyTransformPoint(p: MapPoint, t: ProcrustesTransform): MapPoint {
    const dx = p.x - t.osmCx;
    const dy = p.y - t.osmCy;
    return { x: (dx * t.cosT - dy * t.sinT) * t.scale + t.mapCx, y: (dx * t.sinT + dy * t.cosT) * t.scale + t.mapCy };
}

function applyTransformPoints(points: MapPoint[], t: ProcrustesTransform): MapPoint[] {
    return points.map((p) => applyTransformPoint(p, t));
}

function generateSyntheticRibbon(mapPoints: MapPoint[], osmWidthMeters?: number | null): RibbonData | null {
    if (!mapPoints || mapPoints.length < 10) return null;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of mapPoints) {
        if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }
    const diagonal = Math.hypot(maxX - minX, maxY - minY);

    const halfWidth = osmWidthMeters
        ? diagonal * (osmWidthMeters / 2) / 2000
        : diagonal * 0.004;

    const normals: { x: number; y: number }[] = [];
    for (let i = 0; i < mapPoints.length; i++) {
        const prev = mapPoints[(i - 1 + mapPoints.length) % mapPoints.length];
        const next = mapPoints[(i + 1) % mapPoints.length];
        const dx = next.x - prev.x;
        const dy = next.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        normals.push({ x: -dy / len, y: dx / len });
    }

    return {
        inner: mapPoints.map((p, i) => ({ x: p.x - normals[i].x * halfWidth, y: p.y - normals[i].y * halfWidth })),
        outer: mapPoints.map((p, i) => ({ x: p.x + normals[i].x * halfWidth, y: p.y + normals[i].y * halfWidth })),
    };
}

// ============= DRAWING =============

function drawTrackRibbon(ctx: CanvasRenderingContext2D, ribbon: RibbonData, scalePointFn: (p: MapPoint) => { x: number; y: number }): void {
    if (!ribbon || !ribbon.inner || !ribbon.outer) return;
    if (ribbon.inner.length < 2 || ribbon.outer.length < 2) return;

    ctx.beginPath();
    for (let i = 0; i < ribbon.outer.length; i++) {
        const { x, y } = scalePointFn(ribbon.outer[i]);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    for (let i = ribbon.inner.length - 1; i >= 0; i--) {
        const { x, y } = scalePointFn(ribbon.inner[i]);
        ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(80, 80, 80, 0.9)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(120, 120, 120, 0.4)';
    ctx.lineWidth = 1;
    ctx.stroke();
}

function drawTracksideFeatures(ctx: CanvasRenderingContext2D, features: OsmFeature[], scalePointFn: (p: MapPoint) => { x: number; y: number }): void {
    if (!features || !features.length) return;

    for (const f of features) {
        if (f.polygon && f.polygon.length >= 3) {
            ctx.beginPath();
            for (let i = 0; i < f.polygon.length; i++) {
                const { x, y } = scalePointFn(f.polygon[i]);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.closePath();
            switch (f.type) {
                case 'grandstand': ctx.fillStyle = 'rgba(100, 80, 60, 0.18)'; break;
                case 'water': ctx.fillStyle = 'rgba(40, 60, 120, 0.15)'; break;
                case 'parking': ctx.fillStyle = 'rgba(60, 60, 60, 0.12)'; break;
                default: ctx.fillStyle = 'rgba(80, 80, 100, 0.15)'; break;
            }
            ctx.fill();
        }
        if (f.center && f.type === 'tree') {
            const { x, y } = scalePointFn(f.center);
            const r = Math.max(1.5, (f.radius || 2) * 0.5);
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(40, 100, 40, 0.15)';
            ctx.fill();
        }
    }
}

function filterTrackPaths(paths: SVGPathElement[]): SVGPathElement[] {
    const preferredStrokes = ['#d0e21a', '#ffffff', '#fefefe'];
    const excludedStrokes = ['#c8c8c8', '#000000', '#181818'];

    return paths.filter((path) => {
        const stroke = normalizeColor(getPathStyleValue(path, 'stroke'));
        const fill = normalizeColor(getPathStyleValue(path, 'fill'));
        const strokeWidth = parseFloat(getPathStyleValue(path, 'stroke-width') || '0');

        if (preferredStrokes.includes(stroke)) return true;
        if (excludedStrokes.includes(stroke)) return false;
        if (strokeWidth >= 10 && (fill === 'none' || fill === 'transparent' || fill === '')) return true;
        return false;
    });
}

function getPathStyleValue(path: SVGPathElement, key: string): string {
    const direct = path.getAttribute(key);
    if (direct) return direct;
    const style = path.getAttribute('style');
    if (!style) return '';
    const match = style.match(new RegExp(`${key}\\s*:\\s*([^;]+)`, 'i'));
    return match ? match[1].trim() : '';
}

function normalizeColor(value: string): string {
    if (!value) return '';
    return value.trim().toLowerCase();
}

function extractCornerLabels(textNodes: SVGTextElement[], mapPoints: MapPoint[]): CornerLabel[] {
    const labels: CornerLabel[] = [];
    if (!textNodes.length || !mapPoints.length) return labels;

    textNodes.forEach((node) => {
        const value = (node.textContent || '').trim();
        if (!/^\d+[a-z]?$/i.test(value)) return;
        const x = parseFloat(node.getAttribute('x') || '');
        const y = parseFloat(node.getAttribute('y') || '');
        if (Number.isNaN(x) || Number.isNaN(y)) return;
        const nearest = findNearestPoint({ x, y }, mapPoints);
        if (!nearest) return;
        labels.push({ label: value, point: mapPoints[nearest.index] });
    });

    return labels.sort((a, b) => parseInt(a.label, 10) - parseInt(b.label, 10));
}

function findNearestPoint(target: { x: number; y: number }, points: MapPoint[]): { index: number; dist: number } | null {
    let best: { index: number; dist: number } | null = null;
    points.forEach((point, index) => {
        const dx = point.x - target.x;
        const dy = point.y - target.y;
        const dist = dx * dx + dy * dy;
        if (!best || dist < best.dist) best = { index, dist };
    });
    return best;
}

function applyReplayScaling(canvas: HTMLCanvasElement, points: MapPoint[]): void {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    points.forEach((point) => {
        minX = Math.min(minX, point.x); maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y); maxY = Math.max(maxY, point.y);
    });

    const padding = 30;
    const width = (canvas.clientWidth || canvas.width) - padding * 2;
    const height = (canvas.clientHeight || canvas.height) - padding * 2;
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const scale = Math.min(width / spanX, height / spanY);

    lapReplayState.scale = scale;
    lapReplayState.offsetX = padding - minX * scale + (width - spanX * scale) / 2;
    lapReplayState.offsetY = padding - minY * scale + (height - spanY * scale) / 2;
    lapReplayState.bounds = { minX, maxX, minY, maxY };
}

function drawLapReplay(): void {
    const { canvas, ctx, points, currentIndex, baseMapPoints, cornerLabels } = lapReplayState;
    if (!canvas || !ctx || points.length === 0) return;
    const mapPoints = baseMapPoints.length ? baseMapPoints : points;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#1f1f1f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (lapReplayState.osmAligned) {
        if (lapReplayState.osmAligned.ribbon) drawTrackRibbon(ctx, lapReplayState.osmAligned.ribbon, scalePoint);
        if (lapReplayState.osmAligned.features?.length) drawTracksideFeatures(ctx, lapReplayState.osmAligned.features, scalePoint);
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.beginPath();
    mapPoints.forEach((point, idx) => {
        const { x, y } = scalePoint(point);
        if (idx === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();

    if (currentIndex > 1) {
        ctx.strokeStyle = 'rgba(213, 0, 28, 0.9)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        for (let i = 0; i <= currentIndex; i++) {
            const { x, y } = scalePoint(points[i]);
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
    }

    drawFinishLine(mapPoints);
    drawCornerLabels(cornerLabels);

    const marker = scalePoint(points[currentIndex]);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(marker.x, marker.y, 5, 0, Math.PI * 2);
    ctx.fill();

    drawMiniMap();
    updateLapStats();
}

function updateLapStats(): void {
    const stats = document.getElementById('lapReplayStats');
    if (!stats || !lapReplayState.telemetrySeries.length) return;

    const index = Math.max(0, Math.min(lapReplayState.currentIndex, lapReplayState.telemetrySeries.length - 1));
    const point = lapReplayState.telemetrySeries[index];
    if (!point) return;

    const speed = Number.isFinite(point.speed) ? point.speed.toFixed(1) : '--';
    const gear = Number.isFinite(point.gear) ? String(point.gear) : '--';
    const rpm = Number.isFinite(point.rpm) ? String(Math.round(point.rpm)) : '--';
    const throttle = Number.isFinite(point.throttle) ? String(Math.round(point.throttle * 100)) : '--';
    const brake = Number.isFinite(point.brake) ? String(Math.round(point.brake * 100)) : '--';
    const latG = Number.isFinite(point.latAccel) ? point.latAccel.toFixed(2) : '--';
    const longG = Number.isFinite(point.longAccel) ? point.longAccel.toFixed(2) : '--';

    stats.innerHTML = `
        <div class="lap-stat"><span>Speed</span><strong>${speed}</strong><em>mph</em></div>
        <div class="lap-stat"><span>Gear</span><strong>${gear}</strong></div>
        <div class="lap-stat"><span>RPM</span><strong>${rpm}</strong></div>
        <div class="lap-stat"><span>Throttle</span><strong>${throttle}</strong><em>%</em></div>
        <div class="lap-stat"><span>Brake</span><strong>${brake}</strong><em>%</em></div>
        <div class="lap-stat"><span>Lat G</span><strong>${latG}</strong></div>
        <div class="lap-stat"><span>Long G</span><strong>${longG}</strong></div>
    `;
}

function drawFinishLine(points: MapPoint[]): void {
    if (points.length < 2) return;
    const p0 = scalePoint(points[0]);
    const p1 = scalePoint(points[1]);
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const length = Math.hypot(dx, dy) || 1;
    const nx = -dy / length;
    const ny = dx / length;
    const half = 12;

    const ctx = lapReplayState.ctx;
    if (!ctx) return;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(p0.x - nx * half, p0.y - ny * half);
    ctx.lineTo(p0.x + nx * half, p0.y + ny * half);
    ctx.stroke();
}

function drawCornerLabels(cornerLabels: CornerLabel[]): void {
    if (!cornerLabels || !cornerLabels.length) return;
    const ctx = lapReplayState.ctx;
    if (!ctx) return;
    ctx.font = '12px "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    cornerLabels.forEach((corner) => {
        const pos = scalePoint(corner.point);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(corner.label, pos.x, pos.y + 0.5);
    });
}

function scalePoint(point: MapPoint): { x: number; y: number } {
    const z = lapReplayState.zoom;
    const baseX = point.x * lapReplayState.scale + lapReplayState.offsetX;
    const baseY = point.y * lapReplayState.scale + lapReplayState.offsetY;
    const canvas = lapReplayState.canvas;
    const cx = (canvas ? canvas.clientWidth : 720) / 2;
    const cy = (canvas ? canvas.clientHeight : 400) / 2;
    return { x: (baseX - cx) * z + cx + lapReplayState.panX, y: (baseY - cy) * z + cy + lapReplayState.panY };
}

// ============= ZOOM / PAN =============

function setupReplayZoomPan(canvas: HTMLCanvasElement): void {
    if (lapReplayState.zoomListenersAttached) return;
    lapReplayState.zoomListenersAttached = true;

    canvas.addEventListener('wheel', (e: WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.max(0.5, Math.min(10, lapReplayState.zoom * delta));

        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const cx = canvas.clientWidth / 2;
        const cy = canvas.clientHeight / 2;

        const zRatio = newZoom / lapReplayState.zoom;
        lapReplayState.panX = mx - zRatio * (mx - lapReplayState.panX);
        lapReplayState.panY = my - zRatio * (my - lapReplayState.panY);
        lapReplayState.panX += (cx - mx) * (zRatio - 1);
        lapReplayState.panY += (cy - my) * (zRatio - 1);

        lapReplayState.zoom = newZoom;
        drawLapReplay();
    }, { passive: false });

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
        if (e.button !== 0) return;
        lapReplayState.isPanning = true;
        lapReplayState.panStartX = e.clientX;
        lapReplayState.panStartY = e.clientY;
        lapReplayState.panStartPanX = lapReplayState.panX;
        lapReplayState.panStartPanY = lapReplayState.panY;
        canvas.style.cursor = 'grabbing';
    });

    window.addEventListener('mousemove', (e: MouseEvent) => {
        if (!lapReplayState.isPanning) return;
        lapReplayState.panX = lapReplayState.panStartPanX + (e.clientX - lapReplayState.panStartX);
        lapReplayState.panY = lapReplayState.panStartPanY + (e.clientY - lapReplayState.panStartY);
        drawLapReplay();
    });

    window.addEventListener('mouseup', () => {
        if (lapReplayState.isPanning) {
            lapReplayState.isPanning = false;
            canvas.style.cursor = 'grab';
        }
    });

    let lastTouchDist = 0;
    let lastTouchMid: { x: number; y: number } | null = null;
    canvas.addEventListener('touchstart', (e: TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastTouchDist = Math.hypot(dx, dy);
            lastTouchMid = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2, y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
        } else if (e.touches.length === 1) {
            lapReplayState.isPanning = true;
            lapReplayState.panStartX = e.touches[0].clientX;
            lapReplayState.panStartY = e.touches[0].clientY;
            lapReplayState.panStartPanX = lapReplayState.panX;
            lapReplayState.panStartPanY = lapReplayState.panY;
        }
    }, { passive: false });

    canvas.addEventListener('touchmove', (e: TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            if (lastTouchDist > 0) {
                const tdelta = dist / lastTouchDist;
                lapReplayState.zoom = Math.max(0.5, Math.min(10, lapReplayState.zoom * tdelta));
            }
            const mid = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2, y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
            if (lastTouchMid) {
                lapReplayState.panX += mid.x - lastTouchMid.x;
                lapReplayState.panY += mid.y - lastTouchMid.y;
            }
            lastTouchDist = dist;
            lastTouchMid = mid;
            drawLapReplay();
        } else if (e.touches.length === 1 && lapReplayState.isPanning) {
            lapReplayState.panX = lapReplayState.panStartPanX + (e.touches[0].clientX - lapReplayState.panStartX);
            lapReplayState.panY = lapReplayState.panStartPanY + (e.touches[0].clientY - lapReplayState.panStartY);
            drawLapReplay();
        }
    }, { passive: false });

    canvas.addEventListener('touchend', () => {
        lapReplayState.isPanning = false;
        lastTouchDist = 0;
        lastTouchMid = null;
    });

    canvas.addEventListener('dblclick', (e: MouseEvent) => {
        e.preventDefault();
        lapReplayState.zoom = 1;
        lapReplayState.panX = 0;
        lapReplayState.panY = 0;
        drawLapReplay();
    });

    canvas.style.cursor = 'grab';
}

// ============= REPLAY PLAYBACK =============

let _lastCursorUpdate = 0;
function updateTelemetryCursor(): void {
    const now = performance.now();
    if (now - _lastCursorUpdate < 100) return;
    _lastCursorUpdate = now;
    if (!lapReplayState._telemetryHandle) return;
    const point = lapReplayState.telemetrySeries[lapReplayState.currentIndex];
    if (point && Number.isFinite(point.lapDistPct)) {
        lapReplayState._telemetryHandle.setCursor(point.lapDistPct);
    }
}

function setLapReplayIndex(index: number): void {
    const slider = document.getElementById('lapReplaySlider') as HTMLInputElement | null;
    const timeLabel = document.getElementById('lapReplayTime');
    const clamped = Math.max(0, Math.min(index, lapReplayState.points.length - 1));

    lapReplayState.currentIndex = clamped;
    lapReplayState.elapsedTime = lapReplayState.sampleTimes[clamped] || 0;

    if (slider) slider.value = String(clamped);
    if (timeLabel) timeLabel.textContent = `${lapReplayState.elapsedTime.toFixed(1)}s`;

    drawLapReplay();
    updateTelemetryCursor();
    updateCoachFeedForTime(lapReplayState.elapsedTime, true);
}

function startLapReplay(): void {
    if (lapReplayState.playing) return;

    lapReplayState.playing = true;
    lapReplayState.lastFrameTime = performance.now();
    const toggleBtn = document.getElementById('lapReplayToggle');
    if (toggleBtn) toggleBtn.innerHTML = ICONS.pause + ' Pause';

    const step = (now: number): void => {
        if (!lapReplayState.playing) return;

        const elapsed = (now - lapReplayState.lastFrameTime) / 1000;
        lapReplayState.lastFrameTime = now;
        lapReplayState.elapsedTime += elapsed;

        while (
            lapReplayState.currentIndex < lapReplayState.sampleTimes.length - 1 &&
            lapReplayState.sampleTimes[lapReplayState.currentIndex + 1] <= lapReplayState.elapsedTime
        ) {
            lapReplayState.currentIndex += 1;
        }

        if (lapReplayState.currentIndex >= lapReplayState.sampleTimes.length - 1) {
            stopLapReplay();
            return;
        }

        updateReplayUI();
        lapReplayState.timerId = requestAnimationFrame(step);
    };

    lapReplayState.timerId = requestAnimationFrame(step);
}

function stopLapReplay(): void {
    lapReplayState.playing = false;
    if (lapReplayState.timerId) {
        cancelAnimationFrame(lapReplayState.timerId);
        lapReplayState.timerId = null;
    }
    const toggleBtn = document.getElementById('lapReplayToggle');
    if (toggleBtn) toggleBtn.innerHTML = '&#9654; Play';
}

function updateReplayUI(): void {
    const slider = document.getElementById('lapReplaySlider') as HTMLInputElement | null;
    const timeLabel = document.getElementById('lapReplayTime');
    if (slider) slider.value = String(lapReplayState.currentIndex);
    if (timeLabel) timeLabel.textContent = `${lapReplayState.elapsedTime.toFixed(1)}s`;
    drawLapReplay();
    updateTelemetryCursor();
    updateCoachFeedForTime(lapReplayState.elapsedTime, false);
}

// ============= COACH FEED =============

function buildCoachFeedHtml(): string {
    return `
        <div class="lap-coach">
            <div class="lap-coach-header">
                <h4>${ICONS.brain} Virtual Coach</h4>
                <div class="lap-coach-controls">
                    <button type="button" class="btn-secondary" id="coachVoiceToggle">Voice: On</button>
                    <span id="coachVoiceStatus">Browser TTS</span>
                </div>
            </div>
            <div class="lap-coach-feed" id="lapCoachFeed"></div>
        </div>
    `;
}

function buildCoachMessages(lap: LapData): CoachMessage[] {
    const issues = lap.analysis?.issues || [];
    if (!issues.length) return [];

    const lapSeconds = Number.isFinite(lap.lap_time) ? lap.lap_time : 0;
    return issues.slice(0, 10).map((issue, index) => {
        const time = lapSeconds > 0 ? ((index + 1) / (issues.length + 1)) * lapSeconds : (index + 1) * 5;
        return { time, text: `${issue.name} \u2014 ${formatCoachTip(issue.name)}` };
    });
}

function setupCoachFeed(): void {
    const feed = document.getElementById('lapCoachFeed');
    const toggle = document.getElementById('coachVoiceToggle');
    const status = document.getElementById('coachVoiceStatus');
    if (!feed) return;
    feed.innerHTML = '';

    if (!lapReplayState.coachMessages.length) {
        feed.innerHTML = '<div class="lap-coach-empty">No coach insights for this lap yet.</div>';
        return;
    }

    if (status) status.textContent = 'Browser TTS';

    if (!('speechSynthesis' in window)) {
        lapReplayState.coachVoiceEnabled = false;
        if (toggle) toggle.textContent = 'Voice: Off';
        if (status) status.textContent = 'Voice unavailable';
    }

    if (toggle) {
        toggle.onclick = (): void => {
            lapReplayState.coachVoiceEnabled = !lapReplayState.coachVoiceEnabled;
            toggle.textContent = lapReplayState.coachVoiceEnabled ? 'Voice: On' : 'Voice: Off';
            if (!lapReplayState.coachVoiceEnabled && 'speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
        };
    }

    lapReplayState.coachIndex = 0;
    updateCoachFeedForTime(0, true);
}

function updateCoachFeedForTime(elapsedSeconds: number, reset: boolean): void {
    const feed = document.getElementById('lapCoachFeed');
    if (!feed || !lapReplayState.coachMessages.length) return;

    if (reset) {
        feed.innerHTML = '';
        lapReplayState.coachIndex = 0;
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    }

    while (lapReplayState.coachIndex < lapReplayState.coachMessages.length) {
        const msg = lapReplayState.coachMessages[lapReplayState.coachIndex];
        if (msg.time > elapsedSeconds) break;
        appendCoachMessage(feed, msg);
        if (lapReplayState.playing && lapReplayState.coachVoiceEnabled) speakCoachMessage(msg.text);
        lapReplayState.coachIndex += 1;
    }
}

function appendCoachMessage(container: HTMLElement, message: CoachMessage): void {
    if (!container) return;
    const entry = document.createElement('div');
    entry.className = 'lap-coach-entry';
    // innerHTML hardening: coach message text is internally generated, not user-controlled
    const timeSpan = document.createElement('span');
    timeSpan.className = 'lap-coach-time';
    timeSpan.textContent = formatCoachTime(message.time);
    const textSpan = document.createElement('span');
    textSpan.className = 'lap-coach-text';
    textSpan.textContent = message.text;
    entry.appendChild(timeSpan);
    entry.appendChild(textSpan);
    container.appendChild(entry);
    container.scrollTop = container.scrollHeight;
}

function speakCoachMessage(text: string): void {
    if (!('speechSynthesis' in window)) return;
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = selectCoachVoice();
    if (voice) utterance.voice = voice;
    utterance.rate = 0.96;
    utterance.pitch = 0.9;
    utterance.volume = 1.0;
    window.speechSynthesis.speak(utterance);
}

function selectCoachVoice(): SpeechSynthesisVoice | null {
    if (!('speechSynthesis' in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const preferredNames = ['Microsoft Ryan Online', 'Microsoft Aria Online', 'Google US English', 'Google UK English Male', 'Google UK English Female'];
    for (const name of preferredNames) {
        const match = voices.find((voice) => voice.name.includes(name));
        if (match) return match;
    }
    const english = voices.find((voice) => voice.lang && voice.lang.toLowerCase().startsWith('en'));
    return english || voices[0];
}

function formatCoachTime(seconds: number): string {
    if (!Number.isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1).padStart(4, '0');
    return `${mins}:${secs}`;
}

function formatCoachTip(issueName: string): string {
    const key = String(issueName || '').toLowerCase();
    if (key.includes('oversteer')) return 'aim for smoother throttle to stabilize the rear.';
    if (key.includes('understeer')) return 'release brake earlier and get rotation before apex.';
    if (key.includes('abs') || key.includes('lockup')) return 'brake a touch earlier and reduce pedal spikes.';
    if (key.includes('wheel spin') || key.includes('traction')) return 'feed throttle progressively on exit.';
    if (key.includes('rev limiter')) return 'short-shift to stay in the power band.';
    if (key.includes('steering') && key.includes('jerky')) return 'smooth out steering inputs for better grip.';
    if (key.includes('steering')) return 'reduce mid-corner corrections and trust the line.';
    if (key.includes('tire wear') || key.includes('tire scrub')) return 'avoid sliding and keep inputs calm.';
    if (key.includes('early apex')) return 'delay turn-in to open the exit.';
    if (key.includes('late apex')) return 'turn in slightly earlier for better mid-corner speed.';
    if (key.includes('coasting')) return 'minimize the gap between braking and throttle.';
    if (key.includes('trail brak')) return 'maintain light brake pressure deeper into the corner.';
    if (key.includes('braking instab') || key.includes('brake hop')) return 'brake in a straight line and smoothly release.';
    if (key.includes('throttle')) return 'apply throttle progressively \u2014 smooth is fast.';
    if (key.includes('gear')) return 'review gear selection for each corner.';
    if (key.includes('body roll')) return 'stiffen ARBs for a more stable platform.';
    if (key.includes('oscillat') || key.includes('bounce')) return 'increase damping to settle the car.';
    if (key.includes('brake dive')) return 'increase front compression to control dive.';
    if (key.includes('fuel')) return 'lift and coast on straights to save fuel.';
    if (key.includes('temperature') || key.includes('temp')) return 'manage tire/brake temps through driving style.';
    if (key.includes('pressure')) return 'adjust starting cold pressures for the target range.';
    if (key.includes('apex')) return 'focus on hitting the clipping point consistently.';
    if (key.includes('pace') || key.includes('consistency')) return 'find a rhythm and repeat it every lap.';
    if (key.includes('off track') || key.includes('incident')) return 'review corner approach to stay on track.';
    if (key.includes('ffb') || key.includes('force feedback')) return 'reduce FFB strength for clearer feel.';
    return 'clean up inputs and focus on consistency.';
}

// ============= TELEMETRY CHARTS (Plotly) =============

function drawTelemetryCharts(): void {
    const container = document.getElementById('lapTelemetryPlot');
    const controls = document.getElementById('lapTelemetryControls');
    if (!container || !controls) return;
    if (!lapReplayState.telemetrySeries.length) return;

    const availableSeries = getAvailableTelemetrySeries(lapReplayState.telemetrySeries);
    if (!availableSeries.length) return;

    if (!lapReplayState.visibleTelemetrySeries.length) {
        lapReplayState.visibleTelemetrySeries = availableSeries.map((series) => series.id);
    }

    renderTelemetryControls(controls, availableSeries);
    renderTelemetryPlot(container, availableSeries);
}

function getAvailableTelemetrySeries(points: TelemetryPoint[]): TelemetrySeriesDefinition[] {
    return telemetrySeriesDefinitions.filter((series) =>
        points.some((point) => Number.isFinite(point[series.key]))
    );
}

function renderTelemetryControls(container: HTMLElement, seriesList: TelemetrySeriesDefinition[]): void {
    if (!container) return;

    const selected = new Set(lapReplayState.visibleTelemetrySeries);
    container.innerHTML = seriesList.map((series) => {
        const checked = selected.has(series.id) ? 'checked' : '';
        return `
            <label class="lap-telemetry-toggle" data-series="${series.id}">
                <input type="checkbox" data-series="${series.id}" ${checked}>
                <span class="lap-telemetry-swatch" style="background:${series.color}"></span>
                <span>${escapeHtml(series.label)}</span>
                <span class="lap-telemetry-order">
                    <button type="button" class="lap-telemetry-move" data-move="up" aria-label="Move up">\u25B2</button>
                    <button type="button" class="lap-telemetry-move" data-move="down" aria-label="Move down">\u25BC</button>
                </span>
            </label>
        `;
    }).join('');

    container.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', (event: Event) => {
            const target = event.target as HTMLInputElement;
            const id = target.getAttribute('data-series');
            if (!id) return;
            if (target.checked) {
                if (!lapReplayState.visibleTelemetrySeries.includes(id)) {
                    lapReplayState.visibleTelemetrySeries.push(id);
                }
            } else {
                lapReplayState.visibleTelemetrySeries = lapReplayState.visibleTelemetrySeries.filter((item) => item !== id);
            }
            drawTelemetryCharts();
        });
    });

    container.querySelectorAll('.lap-telemetry-move').forEach((button) => {
        button.addEventListener('click', (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
            const currentTarget = event.currentTarget as HTMLElement;
            const move = currentTarget.getAttribute('data-move');
            const wrapper = currentTarget.closest('.lap-telemetry-toggle');
            if (!wrapper) return;
            const id = wrapper.getAttribute('data-series');
            if (!id) return;
            const order = lapReplayState.visibleTelemetrySeries.slice();
            const index = order.indexOf(id);
            if (index === -1) return;
            const targetIndex = move === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= order.length) return;
            order.splice(index, 1);
            order.splice(targetIndex, 0, id);
            lapReplayState.visibleTelemetrySeries = order;
            drawTelemetryCharts();
        });
    });
}

function renderTelemetryPlot(container: HTMLElement, seriesList: TelemetrySeriesDefinition[]): void {
    if (typeof Plotly === 'undefined') return;

    const orderMap = new Map(lapReplayState.visibleTelemetrySeries.map((id, index) => [id, index]));
    const visibleSeries = seriesList
        .filter((series) => orderMap.has(series.id))
        .sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));

    if (!visibleSeries.length) { container.innerHTML = ''; return; }

    const brakeVisible = visibleSeries.some((s) => s.id === 'brake');
    const hasAbsData = lapReplayState.telemetrySeries.some((point) => point.absActive > 0);
    const overlayAbsOnBrake = brakeVisible && hasAbsData;

    const panelSeries = overlayAbsOnBrake
        ? visibleSeries.filter((s) => s.id !== 'absActive')
        : visibleSeries;

    const sampleTimes = lapReplayState.sampleTimes.length === lapReplayState.telemetrySeries.length
        ? lapReplayState.sampleTimes
        : lapReplayState.telemetrySeries.map((_: TelemetryPoint, index: number) => index / 10);

    const traces: unknown[] = [];
    const layout: Record<string, unknown> = {
        grid: { rows: panelSeries.length, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
        height: Math.max(520, panelSeries.length * 180),
        margin: { l: 60, r: 20, t: 20, b: 30 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: '#1f1f1f',
        showlegend: false,
        dragmode: false,
        hovermode: 'x',
        shapes: [] as unknown[]
    };

    const currentTime = sampleTimes[lapReplayState.currentIndex] || 0;
    (layout.shapes as unknown[]).push({
        type: 'line', x0: currentTime, x1: currentTime, yref: 'paper', y0: 0, y1: 1,
        line: { color: 'rgba(255,255,255,0.6)', width: 1 }
    });

    panelSeries.forEach((series, index) => {
        const axisIndex = index + 1;
        const xaxis = axisIndex === 1 ? 'x' : `x${axisIndex}`;
        const yaxis = axisIndex === 1 ? 'y' : `y${axisIndex}`;
        const values = lapReplayState.telemetrySeries.map((point) => point[series.key] || 0);
        const range = getTelemetryRange(series, lapReplayState.telemetrySeries);

        if (series.id === 'brake' && overlayAbsOnBrake) {
            const absValues = lapReplayState.telemetrySeries.map((point) => point.absActive > 0 ? range.max : null);
            traces.push({
                x: sampleTimes, y: absValues, mode: 'lines', fill: 'tozeroy',
                fillcolor: 'rgba(244, 67, 54, 0.15)', line: { color: 'rgba(244, 67, 54, 0)', width: 0 },
                name: 'ABS Active', xaxis, yaxis, connectgaps: false,
                hovertemplate: 'ABS<extra></extra>', showlegend: false
            });
        }

        traces.push({
            x: sampleTimes, y: values, mode: 'lines',
            line: { color: series.color, width: 2 }, name: series.label,
            xaxis, yaxis, hovertemplate: `%{y:.2f}<extra>${series.label}</extra>`
        });

        if (series.id === 'brake' && overlayAbsOnBrake) {
            const absOnsetX: number[] = [];
            const absOnsetY: number[] = [];
            const telemetry = lapReplayState.telemetrySeries;
            for (let i = 1; i < telemetry.length; i++) {
                const prev = telemetry[i - 1].absActive || 0;
                const curr = telemetry[i].absActive || 0;
                if (curr > 0 && prev === 0) { absOnsetX.push(sampleTimes[i]); absOnsetY.push(telemetry[i].brake || 0); }
            }
            if (absOnsetX.length > 0) {
                traces.push({
                    x: absOnsetX, y: absOnsetY, mode: 'markers',
                    marker: { color: 'rgba(244, 67, 54, 0.8)', size: 5, symbol: 'diamond' },
                    name: 'ABS Triggered', xaxis, yaxis,
                    hovertemplate: 'ABS triggered at %{x:.1f}s<br>Brake: %{y:.0%}<extra></extra>'
                });
            }
        }

        const titleText = series.id === 'brake' && overlayAbsOnBrake ? 'Brake + ABS' : series.label;
        layout[yaxis] = {
            range: [range.min, range.max], showgrid: false, zeroline: false, fixedrange: true,
            tickfont: { color: '#9aa0a6', size: 11 },
            title: { text: titleText, font: { color: '#9aa0a6', size: 11 } }
        };
        layout[xaxis] = { showgrid: false, zeroline: false, fixedrange: true, tickfont: { color: '#9aa0a6', size: 11 } };
    });

    Plotly.react(container, traces, layout, { displayModeBar: false, responsive: true, scrollZoom: false, doubleClick: false });
}

function getTelemetryRange(series: TelemetrySeriesDefinition, points: TelemetryPoint[]): TelemetryRange {
    if (series.range === 'zeroToMax') {
        const maxValue = series.max || Math.max(1, ...points.map((point) => point[series.key] || 0));
        return { min: 0, max: maxValue, span: Math.max(1e-6, maxValue) };
    }
    const values = points.map((point) => point[series.key] || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = Math.max(1e-6, max - min);
    return { min, max, span };
}

// ============= MINI MAP =============

function setupMiniMap(): void {
    if (document.getElementById('miniMap')) return;

    const container = document.createElement('div');
    container.id = 'miniMap';
    container.className = 'mini-map';
    container.innerHTML = `
        <div class="mini-map-header">
            <span>Track View</span>
            <button type="button" class="mini-map-close" aria-label="Close">X</button>
        </div>
        <canvas id="miniMapCanvas" width="240" height="160"></canvas>
    `;

    document.body.appendChild(container);

    const closeBtn = container.querySelector('.mini-map-close');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => {
            miniMapState.dismissed = true;
            container.classList.remove('visible');
            miniMapState.visible = false;
        });
    }

    miniMapState.canvas = container.querySelector('#miniMapCanvas');
    miniMapState.ctx = miniMapState.canvas ? miniMapState.canvas.getContext('2d') : null;

    window.addEventListener('scroll', updateMiniMapVisibility, { passive: true });
    window.addEventListener('resize', updateMiniMapVisibility);
}

function updateMiniMapVisibility(): void {
    const container = document.getElementById('miniMap');
    const replay = document.getElementById('lapReplayContainer');
    const detail = document.getElementById('lapDetailSection');
    if (!container || !replay || !detail || !(detail as HTMLElement).offsetParent) return;

    if (miniMapState.dismissed) { container.classList.remove('visible'); miniMapState.visible = false; return; }

    const rect = replay.getBoundingClientRect();
    const shouldShow = rect.bottom < 0;
    container.classList.toggle('visible', shouldShow);
    miniMapState.visible = shouldShow;
    if (shouldShow) drawMiniMap();
}

function drawMiniMap(): void {
    if (!miniMapState.visible || !miniMapState.canvas || !miniMapState.ctx) return;

    const points = lapReplayState.points;
    if (!points || !points.length) return;

    const mapPoints = lapReplayState.baseMapPoints.length ? lapReplayState.baseMapPoints : points;
    const width = miniMapState.canvas.width;
    const height = miniMapState.canvas.height;

    const scaleData = computeScale(mapPoints, width, height, 12);
    const ctx = miniMapState.ctx;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#141414';
    ctx.fillRect(0, 0, width, height);

    const miniScaleFn = (p: MapPoint): { x: number; y: number } => scalePointWith(scaleData, p);
    if (lapReplayState.osmAligned && lapReplayState.osmAligned.ribbon) {
        drawTrackRibbon(ctx, lapReplayState.osmAligned.ribbon, miniScaleFn);
    } else if (mapPoints.length > 10) {
        const syntheticRibbon = generateSyntheticRibbon(mapPoints);
        if (syntheticRibbon) drawTrackRibbon(ctx, syntheticRibbon, miniScaleFn);
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.beginPath();
    mapPoints.forEach((point, idx) => {
        const { x, y } = scalePointWith(scaleData, point);
        if (idx === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();

    const currentIndex = lapReplayState.currentIndex || 0;
    if (currentIndex > 1) {
        ctx.strokeStyle = 'rgba(213, 0, 28, 0.9)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        for (let i = 0; i <= currentIndex; i++) {
            const { x, y } = scalePointWith(scaleData, points[i]);
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
    }

    const marker = scalePointWith(scaleData, points[currentIndex]);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(marker.x, marker.y, 4, 0, Math.PI * 2);
    ctx.fill();
}

function computeScale(points: MapPoint[], width: number, height: number, padding: number): ScaleData {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    points.forEach((point) => {
        minX = Math.min(minX, point.x); maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y); maxY = Math.max(maxY, point.y);
    });
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY);
    return {
        minX, minY, spanX, spanY, scale,
        offsetX: padding - minX * scale + (width - spanX * scale) / 2,
        offsetY: padding - minY * scale + (height - spanY * scale) / 2
    };
}

function scalePointWith(scaleData: ScaleData, point: MapPoint): { x: number; y: number } {
    return { x: point.x * scaleData.scale + scaleData.offsetX, y: point.y * scaleData.scale + scaleData.offsetY };
}

// ============= DELETE, FILTERS, FORMAT =============

async function deleteLap(lapId: string, event: Event): Promise<void> {
    event.stopPropagation();
    if (!confirm('Are you sure you want to delete this lap?')) return;

    try {
        const response = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'DELETE' }));
        const data = await response.json();
        if (data.success) {
            if (selectedLapId === lapId) { selectedLapId = null; $('#lapDetailSection').hide(); }
            lapsData = data.laps;
            renderLapsGrid();
        } else {
            alert('Failed to delete lap: ' + data.message);
        }
    } catch (error: unknown) {
        console.error('Error deleting lap:', error);
        alert('Error deleting lap: ' + (error instanceof Error ? error.message : String(error)));
    }
}

function initializeLapsFilters(): void {
    $('#searchLaps').on('input', filterLaps);
    $('#filterLapsTrack').on('change', filterLaps);
    $('#filterLapsCar').on('change', filterLaps);
}

function initializeLapsEventHandlers(): void {
    $('#setupPickerModal').on('click', function (this: HTMLElement, e: JQuery.ClickEvent) {
        if (e.target === this) closeSetupPicker();
    });
    $('#setupPickerModal .close').on('click', closeSetupPicker);

    $('#teamIncludeToggle').on('change', async function (this: HTMLInputElement) {
        teamViewState.enabled = $(this).is(':checked');
        const teamSelect = $('#teamSelect');
        teamSelect.prop('disabled', !teamViewState.enabled);
        if (!teamViewState.enabled) {
            teamViewState.teamId = null;
            teamViewState.members = [];
            teamViewState.memberId = null;
            $('#teamMemberSelect').prop('disabled', true).val('');
            stopPresencePolling();
        }
        await loadLaps();
    });

    $('#teamSelect').on('change', async function (this: HTMLSelectElement) {
        const teamId = $(this).val() as string;
        teamViewState.teamId = teamId || null;
        await loadTeamMembers();
        await loadLaps();
    });

    $('#teamMemberSelect').on('change', function (this: HTMLSelectElement) {
        const memberId = $(this).val() as string;
        teamViewState.memberId = memberId || null;
    });

    $('#adminViewAllToggle').on('change', async function (this: HTMLInputElement) {
        adminViewState.enabled = $(this).is(':checked');
        if (adminViewState.enabled) {
            teamViewState.enabled = false;
            $('#teamIncludeToggle').prop('checked', false);
            $('#teamSelect').prop('disabled', true);
            $('#teamMemberSelect').prop('disabled', true);
        }
        await loadLaps();
    });
}

function filterLaps(): void {
    const searchTerm = toSafeString($('#searchLaps').val() as string).toLowerCase();
    const trackFilter = toSafeString($('#filterLapsTrack').val() as string);
    const carFilter = toSafeString($('#filterLapsCar').val() as string);

    $('.lap-card').each(function (this: HTMLElement) {
        const lapId = $(this).data('lap-id') as string;
        const lap = lapsData.find((l) => l.id === lapId);
        if (!lap) return;
        const lapTrack = toSafeString(lap.track).toLowerCase();
        const lapCar = toSafeString(lap.car_name || lap.car_id).toLowerCase();
        const matchesSearch = !searchTerm || lapTrack.includes(searchTerm) || lapCar.includes(searchTerm);
        const matchesTrack = !trackFilter || toSafeString(lap.track) === trackFilter;
        const matchesCar = !carFilter || toSafeString(lap.car_id) === carFilter;
        $(this).toggle(matchesSearch && matchesTrack && matchesCar);
    });
}

function updateLapsTrackFilter(): void {
    const tracks = [...new Set(lapsData.map((l) => toSafeString(l.track).trim()).filter(Boolean))];
    const cars = [...new Set(lapsData.map((l) => toSafeString(l.car_id).trim()).filter(Boolean))];
    const carNames: Record<string, string> = {};
    lapsData.forEach((l) => {
        const carId = toSafeString(l.car_id).trim();
        const carName = toSafeString(l.car_name).trim();
        if (carId && carName) carNames[carId] = carName;
    });

    let trackHtml = '<option value="">All Tracks</option>';
    tracks.forEach((track) => { trackHtml += `<option value="${escapeHtml(track)}">${escapeHtml(track)}</option>`; });
    $('#filterLapsTrack').html(trackHtml);

    let carHtml = '<option value="">All Cars</option>';
    cars.forEach((car) => { carHtml += `<option value="${escapeHtml(car)}">${escapeHtml(carNames[car] || car)}</option>`; });
    $('#filterLapsCar').html(carHtml);
}

function formatLapTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '--:--.---';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const secsStr = secs.toFixed(3).padStart(6, '0');
    return `${mins}:${secsStr}`;
}

function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getConfidenceClass(confidence: number): string {
    if (confidence >= 70) return 'high';
    if (confidence >= 40) return 'medium';
    return 'low';
}

// ============= EXPORTS (first batch) =============

window.initializeLapsTab = initializeLapsTab;
window.lapsData = lapsData;
window.loadLaps = loadLaps;
window.selectLap = selectLap;
window.analyzeLap = analyzeLap;
window.showSetupPicker = showSetupPicker;
window.closeSetupPicker = closeSetupPicker;
window.linkSetup = linkSetup;
window.getRecommendations = getRecommendations;
window.deleteLap = deleteLap;
window.showDrivingRecommendations = showDrivingRecommendations;
window.toggleCornerDetails = toggleCornerDetails;
window.toggleLapGroup = toggleLapGroup;
window.sortLapsTable = sortLapsTable;
window.changeLapPage = changeLapPage;
window.toggleCompareLap = toggleCompareLap;

window.toggleEndpointConnection = toggleEndpointConnection;
window.toggleRecording = toggleRecording;
window.uploadLastLap = uploadLastLap;
window.uploadBestLap = uploadBestLap;
window.saveFilterPreferences = saveFilterPreferences;
window.clearFilterPreferences = clearFilterPreferences;

// ============= IMPORT LAP =============

function openImportLap(): void {
    const modal = document.getElementById('importLapModal');
    if (modal) modal.style.display = 'block';
}

function closeImportLap(): void {
    const modal = document.getElementById('importLapModal');
    if (modal) modal.style.display = 'none';
    const form = document.getElementById('importLapForm') as HTMLFormElement | null;
    if (form) form.reset();
    const status = document.getElementById('importStatus');
    if (status) status.innerHTML = '';
}

async function importLap(formData: FormData): Promise<void> {
    const statusEl = document.getElementById('importStatus');
    const submitBtn = document.getElementById('importSubmitBtn') as HTMLButtonElement | null;

    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Importing...'; }
    if (statusEl) statusEl.innerHTML = '<div class="status-loading">Importing lap data...</div>';

    try {
        const response = await fetch(apiUrl('/api/laps?action=import'), authFetchOptions({ method: 'POST', body: formData }));
        const data = await response.json();

        if (data.success) {
            if (statusEl) {
                statusEl.innerHTML = `
                    <div class="status-success">
                        ${ICONS.checkCircle} ${escapeHtml(data.message)}<br>
                        <small>Data points: ${data.data_points} | Format: ${escapeHtml(String(data.format).toUpperCase())}</small>
                    </div>
                `;
            }
            setTimeout(() => { closeImportLap(); loadLaps(); }, 1500);
        } else {
            throw new Error(data.message);
        }
    } catch (error: unknown) {
        if (statusEl) {
            statusEl.innerHTML = `
                <div class="status-error">
                    ${ICONS.xCircle} Import failed: ${escapeHtml(error instanceof Error ? error.message : String(error))}
                </div>
            `;
        }
    } finally {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Import Lap'; }
    }
}

async function initializeApiToken(): Promise<void> {
    if (!shouldInitializeLapsEndpoint()) return;

    try {
        const savedToken = localStorage.getItem('apiToken');
        if (savedToken) { setApiToken(savedToken); return; }

        const response = await fetch(getEndpointUrl("/tts/auth/verify"));
        if (response.ok) {
            const data = parseEndpointResponse(await response.json());
            if (data.token) {
                setApiToken(data.token as string);
                console.log('API token loaded from Windows endpoint:', String(data.token).substring(0, 12) + '...');
            }
        }
    } catch (error: unknown) {
        console.warn('Could not load API token from Windows endpoint:', error);
    }
}

// Initialize import form submission
document.addEventListener('DOMContentLoaded', function (): void {
    if (!isLapsUIPresent()) return;

    initializeApiToken();

    const importForm = document.getElementById('importLapForm');
    if (importForm) {
        importForm.addEventListener('submit', function (e: Event): void {
            e.preventDefault();
            const formData = new FormData(this as HTMLFormElement);
            importLap(formData);
        });
    }

    const importBtn = document.getElementById('importLapBtn');
    if (importBtn) {
        importBtn.addEventListener('click', openImportLap);
    }

    window.addEventListener('click', function (event: MouseEvent): void {
        const modal = document.getElementById('importLapModal');
        if (event.target === modal) closeImportLap();
    });
});

// Cleanup all intervals on page unload / navigation
function cleanupAllIntervals(): void {
    stopEndpointAvailabilityCheck();
    if (endpointPollingInterval) { clearInterval(endpointPollingInterval); endpointPollingInterval = null; }
    if (presenceInterval) { clearInterval(presenceInterval); presenceInterval = null; }
    if (presencePingInterval) { clearInterval(presencePingInterval); presencePingInterval = null; }
}
window.addEventListener('beforeunload', cleanupAllIntervals);
document.addEventListener('visibilitychange', () => { if (document.hidden) cleanupAllIntervals(); });

window.openImportLap = openImportLap;
window.closeImportLap = closeImportLap;

// ============= COACHING MEMORY INTEGRATION =============

let coachingMemory: CoachingMemory | null = null;

async function initializeCoachingMemory(trackId: string, trackName: string, carId: string | null, carName: string | null): Promise<unknown> {
    if (!trackId) return null;

    if (!coachingMemory) {
        coachingMemory = new CoachingMemory({ apiBaseUrl: '/api/coaching_memory' });
    }

    coachingMemory.setContext(trackId, carId);

    try {
        await analyzePreviousLapsForCoaching(trackId, carId, trackName, carName);
    } catch (error: unknown) {
        console.log('[Coaching] Could not analyze previous laps:', error instanceof Error ? error.message : String(error));
    }

    try {
        const result = await coachingMemory.startSession({
            trackName: trackName,
            carName: carName || undefined,
            autoInit: true,
            tts: false,
            onWelcome: (message: string, session: CoachingSessionData) => {
                displayCoachingMemoryWelcome(message, session);
            }
        });

        if (result.session) {
            displayCoachingMemoryWelcome(result.session.welcomeMessage || '', result.session);
        }
        return result;
    } catch (error: unknown) {
        console.error('Failed to initialize coaching memory:', error);
        return null;
    }
}

async function analyzePreviousLapsForCoaching(trackId: string, carId: string | null, _trackName: string, _carName: string | null): Promise<{ analyzed: number; imported: number }> {
    console.log('[Coaching] Checking for unanalyzed laps:', { trackId, carId });

    const response = await fetch(buildMyLapsUrl(), { method: 'GET', headers: getAuthHeaders() });
    const data = await response.json();
    if (!data.success || !data.laps) return { analyzed: 0, imported: 0 };

    const unanalyzedLaps: LapData[] = data.laps.filter((lap: LapData) => {
        const lapTrack = (lap.track || '').toLowerCase();
        const targetTrack = trackId.toLowerCase();
        const trackMatch = lapTrack.includes(targetTrack) || targetTrack.includes(lapTrack);
        if (!trackMatch) return false;
        if (carId) {
            const lapCar = (lap.car_id || '').toLowerCase();
            const targetCar = carId.toLowerCase();
            const carMatch = lapCar.includes(targetCar) || targetCar.includes(lapCar);
            if (!carMatch) return false;
        }
        return !lap.analysis;
    });

    if (unanalyzedLaps.length === 0) {
        console.log('[Coaching] No unanalyzed laps found');
        return { analyzed: 0, imported: 0 };
    }

    console.log(`[Coaching] Found ${unanalyzedLaps.length} unanalyzed laps, analyzing...`);

    let analyzedCount = 0;
    const importedCount = 0;

    for (const lap of unanalyzedLaps.slice(0, 5)) {
        try {
            if (!lap.csv_data) {
                const lapResponse = await fetch(resolveLapUrl(lap.id), { method: 'GET', headers: getAuthHeaders() });
                const lapDataResp = await lapResponse.json();
                if (!lapDataResp.success || !lapDataResp.lap?.csv_data) continue;
                lap.csv_data = lapDataResp.lap.csv_data;
            }

            const formData = new FormData();
            formData.append('csv_data', lap.csv_data!);

            const analysisResponse = await fetch(apiUrl('analyze_telemetry_api'), { method: 'POST', body: formData });
            const analysis = await analysisResponse.json();

            if (analysis.error) { console.warn(`[Coaching] Analysis failed for lap ${lap.id}:`, analysis.error); continue; }

            await fetch(apiUrl('/api/laps?action=store_analysis'), {
                method: 'POST', headers: getAuthHeaders(),
                body: JSON.stringify({ lap_id: lap.id, analysis: analysis })
            });

            analyzedCount++;
            console.log(`[Coaching] Analyzed lap ${lap.id}: ${analysis.issues?.length || 0} issues found`);
        } catch (error: unknown) {
            console.warn(`[Coaching] Failed to analyze lap ${lap.id}:`, error instanceof Error ? error.message : String(error));
        }
    }

    console.log(`[Coaching] Analyzed ${analyzedCount} laps`);
    return { analyzed: analyzedCount, imported: importedCount };
}

function displayCoachingMemoryWelcome(message: string, session: CoachingSessionData): void {
    const feed = document.getElementById('lapCoachFeed');
    if (!feed) return;

    const existingMemory = feed.querySelector('.coach-memory-welcome');
    if (existingMemory) existingMemory.remove();

    const welcomeEl = document.createElement('div');
    welcomeEl.className = 'coach-memory-welcome';

    let html = `<div class="memory-welcome-message">${ICONS.brain} ${escapeHtml(message)}</div>`;

    if (session.top3 && session.top3.length > 0) {
        html += '<div class="memory-top3"><strong>Top Focus Areas:</strong><ol>';
        session.top3.forEach((item) => {
            html += `<li>${escapeHtml(item.description)}`;
            if (item.location && item.location !== 'General') {
                html += ` <span class="memory-location">(${escapeHtml(item.location)})</span>`;
            }
            if (item.priority === 'high' || item.priority === 'critical') {
                html += ' <span class="memory-priority">' + ICONS.alertTriangle + ' HIGH</span>';
            }
            html += '</li>';
        });
        html += '</ol></div>';
    }

    html += `<div class="memory-session-info">Session #${session.sessionNumber || 1}`;
    if (session.hasPreviousData) html += ` \u00B7 Continuing from previous data`;
    html += '</div>';

    welcomeEl.innerHTML = html;
    feed.insertBefore(welcomeEl, feed.firstChild);
}

async function startCoachingSession(trackId: string, trackName: string): Promise<void> {
    let carId: string | null = null;
    let carName: string | null = null;

    if (endpointSession) {
        carId = endpointSession.car?.id || endpointSession.car?.name || null;
        carName = endpointSession.car?.display_name || endpointSession.car?.name || null;
    }

    if (typeof coachingTracker !== 'undefined') {
        coachingTracker.setTrack(trackId, trackName);
    }

    await initializeCoachingMemory(trackId, trackName, carId, carName);

    if (typeof window.onCoachingSessionStarted === 'function') {
        window.onCoachingSessionStarted(trackId, trackName);
    }

    console.log(`Coaching session started: ${trackName} (${trackId}) with ${carName || 'unknown car'}`);
}

function startCoachingLap(lapNumber: number): void {
    if (typeof coachingTracker !== 'undefined') {
        coachingTracker.startLap(lapNumber);
    }
}

async function populateCoachingFromAnalyzedLap(lapId: string): Promise<CoachingImportResult> {
    if (!coachingMemory) return { success: false, error: 'Coaching memory not initialized' };

    try {
        const response = await fetch(resolveLapUrl(lapId), authFetchOptions({ method: 'GET' }));
        const data = await response.json();
        if (!data.success || !data.lap) return { success: false, error: 'Lap not found' };

        const lap: LapData = data.lap;
        const analysis = lap.analysis;
        if (!analysis) return { success: false, error: 'No analysis available' };

        let issuesImported = 0;

        if (analysis.driving_recommendations && analysis.driving_recommendations.corners) {
            for (const corner of analysis.driving_recommendations.corners) {
                if (corner.issues_found && corner.issues_found.length > 0) {
                    for (const issue of corner.issues_found) {
                        if (issue.severity >= 60) {
                            await coachingMemory.addTopic(
                                issue.type || 'corner_issue',
                                `${issue.name} at corner ${corner.corner_number}`,
                                issue.severity >= 80 ? 'high' : 'medium',
                                { corner: corner.corner_number, location_pct: corner.location?.start_pct, phase: issue.phase, source: 'lap_analysis' }
                            );
                            issuesImported++;
                        }
                    }
                }
            }
        }

        if (analysis.issue_keys && analysis.issue_keys.length > 0) {
            for (const issueKey of analysis.issue_keys) {
                const topics = await coachingMemory.getTopics('active');
                const exists = topics.topics.some((t: CoachingTopic) => t.issueType === issueKey);
                if (!exists) {
                    await coachingMemory.addTopic(issueKey, formatIssueDescription(issueKey), 'medium', { source: 'analysis_summary' });
                    issuesImported++;
                }
            }
        }

        const top3Result = await coachingMemory.getTop3();
        return { success: true, issuesImported, top3: top3Result.top3 };
    } catch (error: unknown) {
        console.error('Failed to populate coaching memory:', error);
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
}

function formatIssueDescription(issueKey: string): string {
    const descriptions: Record<string, string> = {
        'high_speed_understeer': 'High-speed understeer - reduce front wing or soften front ARB',
        'low_speed_understeer': 'Low-speed understeer - check differential or soften front springs',
        'high_speed_oversteer': 'High-speed oversteer - increase rear wing or stiffen rear',
        'low_speed_oversteer': 'Low-speed oversteer - check differential or soften rear springs',
        'unstable_high_speed': 'High-speed instability - increase rear wing or check aero balance',
        'poor_turn_in': 'Poor turn-in response - stiffen front ARB or reduce front ride height',
        'excessive_abs': 'Excessive ABS activation - adjust brake bias forward',
        'wheel_spin': 'Wheel spin on exit - reduce throttle sensitivity or stiffen rear springs',
        'bottoming_out': 'Bottoming out - increase ride height or stiffen springs',
        'loose_over_bumps': 'Loose over bumps - soften bump stiffness or raise ride height',
        'on_throttle_oversteer': 'Oversteer on throttle - check differential settings',
        'lift_off_oversteer': 'Lift-off oversteer - check rear ARB or differential preload',
        'brake_hop': 'Brake hop - check brake bias or front bump stiffness',
        'aero_sensitivity': 'Aero sensitivity - adjust wing balance or ride heights',
        'tire_wear': 'High tire wear - check camber or driving style',
        'fuel_mileage': 'Poor fuel mileage - adjust fuel map or driving style',
        'steering_feel': 'Steering feel issues - check caster or front camber',
        'weight_transfer': 'Weight transfer issues - adjust ARB balance',
        'brake_temperature_management': 'Brake temperature issues - adjust cooling or driving style',
        'bump_steering': 'Bump steer - check suspension geometry',
        'rear_wheel_spin': 'Rear wheel spin - increase diff preload or raise traction control',
        'front_wheel_lockup': 'Front wheel lockup - move brake bias rearward or increase ABS',
        'oversteer_from_audio': 'Oversteer detected from tire audio - soften rear ARB or reduce diff preload',
        'suspension_oscillation': 'Suspension oscillation - increase bump damping to settle the car',
        'excessive_body_roll': 'Excessive body roll - stiffen anti-roll bars',
        'excessive_brake_dive': 'Excessive brake dive - increase front compression damping',
        'shock_travel_limit': 'Shock hitting travel limit - raise ride height or stiffen springs',
        'pitch_gradient_excessive': 'Excessive pitch under braking - increase front compression damping',
        'shock_histogram_imbalance': 'Shock velocity imbalance - rebalance damper settings',
        'incorrect_gear_selection': 'Wrong gear for corner - review gear selection for each turn',
        'coasting_time_loss': 'Time lost coasting - minimize gap between brake release and throttle',
        'suboptimal_trail_braking': 'Trail braking needs work - maintain brake pressure deeper into corner',
        'rev_limiter_abuse': 'Hitting rev limiter - short-shift to stay in power band',
        'early_apex': 'Early apex tendency - delay turn-in to open the exit',
        'late_apex': 'Late apex tendency - turn in slightly earlier for better mid-corner speed',
        'premature_throttle': 'Getting on throttle too early - wait for car to rotate before applying power',
        'braking_instability': 'Braking instability - adjust brake bias or increase front compression',
        'poor_throttle_modulation': 'Rough throttle application - apply throttle more progressively on exit',
        'traction_control_overuse': 'TC activating too much - lower TC and improve throttle modulation',
        'consistent_grip_limit': 'Consistently over grip limit - reduce corner entry speed slightly',
        'inconsistent_braking_points': 'Inconsistent braking points - use reference markers for braking zones',
        'braking_efficiency_low': 'Low braking efficiency - brake harder initially then trail off smoothly',
        'coasting_in_gear': 'Coasting in gear - engine braking is available, use it or downshift',
        'racing_line_suboptimal': 'Racing line needs improvement - review ideal line for key corners',
        'apex_accuracy_low': 'Missing apexes - focus on hitting the clipping point consistently',
        'steering_input_jerky': 'Jerky steering inputs - smooth out steering for better grip and control',
        'throttle_input_rough': 'Rough throttle inputs - smooth out throttle transitions',
        'roll_gradient_imbalance': 'Roll gradient imbalance - adjust front/rear ARB ratio',
        'traction_circle_underutilized': 'Not using full grip - combine braking/turning/acceleration more',
        'yaw_rate_mismatch': 'Yaw rate mismatch - adjust rear ARB or differential settings',
        'tire_temperature_imbalance': 'Tire temp imbalance - adjust camber to even temperatures',
        'excessive_tire_scrubbing': 'Excessive tire scrubbing - check camber and toe settings',
        'understeer_from_audio': 'Understeer detected from tire audio - soften front ARB or springs',
        'tire_pressure_low': 'Low tire pressure - increase starting cold pressures',
        'tire_pressure_high': 'High tire pressure - decrease starting cold pressures',
        'tire_wear_uneven': 'Uneven tire wear - adjust camber for even wear distribution',
        'engine_warning': 'Engine warning active - monitor temps and reduce load if needed',
        'high_water_temp': 'High water temperature - increase cooling or reduce pace',
        'low_oil_pressure': 'Low oil pressure - pit immediately to prevent engine damage',
        'fuel_consumption_high': 'High fuel consumption - lift and coast or reduce downforce drag',
        'fuel_low': 'Low fuel level - conserve fuel or plan pit stop',
        'fuel_strategy_pit_needed': 'Pit stop needed for fuel - plan pit window',
        'fuel_saving_required': 'Fuel saving mode required - lift and coast on straights',
        'pace_inconsistent': 'Inconsistent pace - focus on repeatable inputs and rhythm',
        'brake_bias_suboptimal': 'Brake bias not optimal - adjust for current conditions',
        'tc_setting_high': 'TC set too high - lower for better exit speed',
        'off_track_detected': 'Off-track excursion detected - review corner approach',
        'pit_speed_violation': 'Pit speed limit violation - slow down in pit lane',
        'delta_time_loss': 'Losing time in sector - compare with best lap for improvements',
        'optimal_lap_projection': 'Optimal lap available - combine best sectors for faster time',
        'weather_impact_handling': 'Weather affecting handling - adjust TC and brake bias for conditions',
        'track_temp_evolution': 'Track temperature changed - tire pressures may need adjustment',
        'ffb_clipping': 'Force feedback clipping - reduce FFB strength for better feel'
    };

    return descriptions[issueKey] || issueKey.replace(/_/g, ' ');
}

async function getCurrentCoachingFocus(): Promise<CoachingTopic[]> {
    if (!coachingMemory) return [];
    try {
        const result = await coachingMemory.getTop3();
        return result.top3 || [];
    } catch (error: unknown) {
        console.error('Failed to get coaching focus:', error);
        return [];
    }
}

async function markCoachingTopicImproved(topicId: string, notes: string = ''): Promise<unknown> {
    if (!coachingMemory) return { success: false, error: 'Not initialized' };
    try {
        const result = await coachingMemory.markImproved(topicId, notes);
        const top3 = await coachingMemory.getTop3();
        const session = coachingMemory.currentMemory as CoachingSessionData | null;
        if (session && top3.top3) {
            session.top3 = top3.top3;
            displayCoachingMemoryWelcome('Great progress! Updated focus areas:', session);
        }
        return result;
    } catch (error: unknown) {
        console.error('Failed to mark topic improved:', error);
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
}

async function showCoachingMemory(): Promise<void> {
    if (!coachingMemory) { alert('Coaching memory not initialized. Start a session first.'); return; }

    try {
        const [status, topics] = await Promise.all([
            coachingMemory.getStatus(),
            coachingMemory.getTopics('all')
        ]);

        let html = `
            <div class="modal coaching-memory-modal">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3>${ICONS.brain} Coaching Memory</h3>
                        <button type="button" class="close" onclick="closeCoachingMemoryModal()">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="memory-stats">
                            <div class="stat-item"><span class="stat-value">${status.stats.totalTopics}</span><span class="stat-label">Total Topics</span></div>
                            <div class="stat-item"><span class="stat-value">${status.stats.activeTopics}</span><span class="stat-label">Active</span></div>
                            <div class="stat-item"><span class="stat-value">${status.stats.improvedTopics}</span><span class="stat-label">Improved</span></div>
                            <div class="stat-item"><span class="stat-value">${status.stats.sessionCount}</span><span class="stat-label">Sessions</span></div>
                        </div>
                        <div class="memory-top3-section">
                            <h4>Current Top 3 Focus Areas</h4>
                            ${status.top3.length > 0 ? `
                                <ol class="top3-list">
                                    ${status.top3.map((item: CoachingTopic) => `
                                        <li class="priority-${item.priority}">
                                            <span class="topic-description">${escapeHtml(item.description)}</span>
                                            ${item.location ? `<span class="topic-location">${escapeHtml(item.location)}</span>` : ''}
                                            <button type="button" class="btn-small" onclick="markCoachingTopicImproved('${item.id}', 'Marked as improved via UI')">\u2713 Fixed</button>
                                        </li>
                                    `).join('')}
                                </ol>
                            ` : '<p class="empty-state">All focus areas addressed! Great job!</p>'}
                        </div>
                        <div class="memory-all-topics">
                            <h4>All Topics</h4>
                            <div class="topics-list">
                                ${topics.topics.map((topic: CoachingTopic) => `
                                    <div class="topic-item status-${topic.status || 'active'} priority-${topic.priority}">
                                        <span class="topic-type">${escapeHtml(String(topic.issueType || ''))}</span>
                                        <span class="topic-description">${escapeHtml(String(topic.description || ''))}</span>
                                        <span class="topic-status">${escapeHtml(String(topic.status || 'active'))}</span>
                                    </div>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn-secondary" onclick="initializeCoachingMemoryFromLaps()">Import from Laps</button>
                        <button type="button" class="btn-danger" onclick="clearCoachingMemory()">Clear Memory</button>
                        <button type="button" class="btn-primary" onclick="closeCoachingMemoryModal()">Close</button>
                    </div>
                </div>
            </div>
        `;

        const modalContainer = document.createElement('div');
        modalContainer.id = 'coachingMemoryModal';
        modalContainer.innerHTML = html;
        document.body.appendChild(modalContainer);
    } catch (error: unknown) {
        console.error('Failed to show coaching memory:', error);
        alert('Failed to load coaching memory: ' + (error instanceof Error ? error.message : String(error)));
    }
}

function closeCoachingMemoryModal(): void {
    const modal = document.getElementById('coachingMemoryModal');
    if (modal) modal.remove();
}

async function initializeCoachingMemoryFromLaps(): Promise<void> {
    if (!coachingMemory) return;
    try {
        const result = await coachingMemory.initializeFromLaps();
        alert(`Imported ${result.topics?.length || 0} topics from lap data`);
        closeCoachingMemoryModal();
        showCoachingMemory();
    } catch (error: unknown) {
        alert('Failed to import: ' + (error instanceof Error ? error.message : String(error)));
    }
}

async function clearCoachingMemory(): Promise<void> {
    if (!coachingMemory) return;
    if (!confirm('Are you sure you want to clear all coaching memory for this track?')) return;
    try {
        await coachingMemory.clear();
        closeCoachingMemoryModal();
        alert('Coaching memory cleared');
    } catch (error: unknown) {
        alert('Failed to clear memory: ' + (error instanceof Error ? error.message : String(error)));
    }
}

async function generateCoachingSessionSummary(): Promise<CoachingSessionSummary | null> {
    if (!coachingTracker || !coachingTracker.currentTrackId) return null;

    try {
        const analysis = await coachingTracker.getAnalysis();
        if (!analysis.success) { console.log('[Coaching] No analysis data available for summary'); return null; }

        const stats = analysis.analysis?.stats || {};
        const patterns = analysis.analysis?.patterns || [];
        const multiLocationIssues = analysis.analysis?.multiLocationIssues || [];

        const summary: CoachingSessionSummary = {
            trackId: coachingTracker.currentTrackId,
            trackName: coachingTracker.currentTrackName,
            lapsAnalyzed: stats.lapsAnalyzed || 0,
            totalIssues: stats.totalIssues || 0,
            patternCount: stats.patternCount || 0,
            multiLocationCount: stats.multiLocationCount || 0,
            topPatterns: patterns.slice(0, 3),
            multiLocationIssues: multiLocationIssues.slice(0, 2),
            timestamp: new Date().toISOString()
        };

        console.log('[Coaching] Session summary generated:', summary);
        return summary;
    } catch (error: unknown) {
        console.error('[Coaching] Failed to generate session summary:', error);
        return null;
    }
}

async function endCoachingSession(): Promise<CoachingSessionSummary | null> {
    console.log('[Coaching] Ending coaching session...');

    const summary = await generateCoachingSessionSummary();

    if (typeof coachingTracker !== 'undefined' && coachingTracker.sessionIssues?.length > 0) {
        await coachingTracker.submitLapIssues();
    }

    if (coachingMemory) {
        coachingMemory.sessionActive = false;
    }

    if (summary && summary.totalIssues > 0) {
        let message = ICONS.flag + ` Session Summary: ${summary.trackName || summary.trackId}\n\n`;
        message += `Laps analyzed: ${summary.lapsAnalyzed}\n`;
        message += `Issues recorded: ${summary.totalIssues}\n`;
        message += `Patterns detected: ${summary.patternCount}\n`;

        if (summary.multiLocationCount > 0) {
            message += '\n' + ICONS.alertTriangle + ` Multi-location issues: ${summary.multiLocationCount}\n`;
            summary.multiLocationIssues.forEach((issue) => {
                message += `  \u2022 ${issue.name} (${issue.locationCount} locations)\n`;
            });
        }

        if (summary.topPatterns.length > 0) {
            message += '\n' + ICONS.barChart + ` Top patterns:\n`;
            summary.topPatterns.forEach((pattern, i) => {
                const location = pattern.cornerNumber > 0 ? `Turn ${pattern.cornerNumber}` : `${Math.round(pattern.position * 100)}%`;
                message += `  ${i + 1}. ${pattern.type} at ${location} (${pattern.count}x)\n`;
            });
        }

        message += '\n' + ICONS.lightbulb + ` Tips have been saved to coaching memory!`;
        showCoachingSessionNotification(message);
    }

    if (typeof coachingTracker !== 'undefined') {
        coachingTracker.currentTrackId = null;
        coachingTracker.currentTrackName = null;
        coachingTracker.currentLap = 0;
    }

    coachingMemory = null;
    return summary;
}

function showCoachingSessionNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'coaching-session-notification';

    const header = document.createElement('div');
    header.className = 'notification-header';
    const headerSpan = document.createElement('span');
    headerSpan.innerHTML = `${ICONS.flag} Coaching Session Complete`;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u00D7';
    closeBtn.onclick = (): void => { notification.remove(); };
    header.appendChild(headerSpan);
    header.appendChild(closeBtn);

    const body = document.createElement('pre');
    body.className = 'notification-body';
    body.textContent = message;

    notification.appendChild(header);
    notification.appendChild(body);

    notification.style.cssText = `
        position: fixed; top: 20px; right: 20px; max-width: 400px; max-height: 80vh;
        overflow-y: auto; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 2px solid #d5001c; border-radius: 12px; padding: 16px; color: #fff;
        z-index: 10000; box-shadow: 0 8px 32px rgba(0,0,0,0.5); font-family: monospace;
        font-size: 13px; line-height: 1.5;
    `;

    document.body.appendChild(notification);
    setTimeout(() => { if (notification.parentElement) notification.remove(); }, 30000);
}

// Export coaching memory functions
window.initializeCoachingMemory = initializeCoachingMemory;
window.startCoachingSession = startCoachingSession;
window.startCoachingLap = startCoachingLap;
window.populateCoachingFromAnalyzedLap = populateCoachingFromAnalyzedLap;
window.getCurrentCoachingFocus = getCurrentCoachingFocus;
window.markCoachingTopicImproved = markCoachingTopicImproved;
window.showCoachingMemory = showCoachingMemory;
window.closeCoachingMemoryModal = closeCoachingMemoryModal;
window.initializeCoachingMemoryFromLaps = initializeCoachingMemoryFromLaps;
window.clearCoachingMemory = clearCoachingMemory;
window.endCoachingSession = endCoachingSession;
window.generateCoachingSessionSummary = generateCoachingSessionSummary;
window.checkAndSyncCoachingStatus = checkAndSyncCoachingStatus;
window.showCoachingNotification = showCoachingNotification;
window.analyzePreviousLapsForCoaching = analyzePreviousLapsForCoaching;

// ============= CSS STYLES =============

const coachingMemoryStyles = document.createElement('style');
coachingMemoryStyles.textContent = `
    .coach-memory-welcome {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 1px solid #d5001c; border-radius: 8px; padding: 16px; margin-bottom: 16px; color: #fff;
    }
    .memory-welcome-message { font-size: 1.1em; font-weight: 500; margin-bottom: 12px; color: #fff; }
    .memory-top3 { margin: 12px 0; }
    .memory-top3 ol { margin: 8px 0; padding-left: 24px; }
    .memory-top3 li { margin: 6px 0; color: #e0e0e0; }
    .memory-location { color: #9aa0a6; font-size: 0.9em; }
    .memory-priority { color: #ff5252; font-size: 0.85em; font-weight: bold; }
    .memory-session-info { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.1); font-size: 0.9em; color: #9aa0a6; }
    .coaching-memory-modal .modal-content { max-width: 700px; max-height: 80vh; overflow-y: auto; }
    .memory-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; padding: 16px; background: #1f1f1f; border-radius: 8px; }
    .stat-item { text-align: center; }
    .stat-value { display: block; font-size: 1.8em; font-weight: bold; color: #d5001c; }
    .stat-label { display: block; font-size: 0.85em; color: #9aa0a6; margin-top: 4px; }
    .memory-top3-section { margin-bottom: 24px; }
    .memory-top3-section h4 { margin-bottom: 12px; color: #fff; }
    .top3-list li { display: flex; align-items: center; gap: 12px; padding: 12px; margin: 8px 0; background: #1f1f1f; border-radius: 6px; border-left: 3px solid #d5001c; }
    .top3-list li.priority-high { border-left-color: #ff5252; }
    .topic-description { flex: 1; }
    .topic-location { color: #9aa0a6; font-size: 0.9em; }
    .memory-all-topics h4 { margin-bottom: 12px; color: #fff; }
    .topics-list { max-height: 300px; overflow-y: auto; }
    .topic-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; margin: 4px 0; background: #1f1f1f; border-radius: 4px; }
    .topic-item.status-improved { opacity: 0.6; }
    .topic-item.status-improved .topic-description { text-decoration: line-through; }
    .topic-type { font-size: 0.8em; color: #9aa0a6; text-transform: uppercase; min-width: 100px; }
    .topic-status { font-size: 0.8em; padding: 2px 8px; border-radius: 4px; background: #333; }
    .topic-item.status-improved .topic-status { background: #2e7d32; }
    @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    @keyframes slideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
    .coaching-notification-toast .notification-title { font-weight: bold; margin-bottom: 4px; }
    .coaching-notification-toast .notification-message { font-size: 0.9em; opacity: 0.9; }
`;
document.head.appendChild(coachingMemoryStyles);

// ============= PIT REVIEW POPUP =============

let _pitReviewWindow: Window | null = null;

function openPitReviewPopup(): void {
    if (_pitReviewWindow && !_pitReviewWindow.closed) {
        _pitReviewWindow.focus();
        try { _pitReviewWindow.location.reload(); } catch (_e: unknown) { /* cross-origin */ }
        return;
    }

    const width = 1200;
    const height = 900;
    const left = Math.max(0, (screen.width - width) / 2);
    const top = Math.max(0, (screen.height - height) / 2);

    _pitReviewWindow = window.open(
        '/pit-review',
        'pitReview',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!_pitReviewWindow) {
        console.log('[PitReview] Popup blocked \u2014 use the manual button');
    }
}

window.openPitReviewPopup = openPitReviewPopup;
